import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Spin, Card, Radio, Table, Select, Button, Tabs, Row, Col, Checkbox, Tag } from 'antd';
import { FullscreenOutlined, FullscreenExitOutlined, LeftOutlined, RightOutlined, CalendarOutlined, LineChartOutlined, RocketOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { ComposedChart, Line, LineChart, Area, AreaChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Brush, BarChart, Bar, Cell, PieChart, Pie, ReferenceLine, LabelList } from 'recharts';
import GlobalFilterBar from '../components/common/GlobalFilterBar';
import DownloadButton from '../components/common/DownloadButton';
import { useFilters } from '../context/FilterContext';
import { formatChartDateAxis, formatCurrencyKM, tooltipSorter, aggregateChartData, formatTableData, getTableColumns, formatTooltipValue } from '../utils/formatters';
import { exportCardAsPng, exportTableAsCsv } from '../utils/downloadUtils';
import { getRevenueAnalysis, getContracts, getMarketDashboard } from '../services/api';
import dayjs from 'dayjs';

const formatValueKM = (value, minimumFractionDigits = 2) => {
  if (value === null || value === undefined || isNaN(value)) return '';
  const num = Number(value);
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  const fmt = (val) => val.toLocaleString('en-IN', {
    minimumFractionDigits,
    maximumFractionDigits: minimumFractionDigits
  });

  if (absNum >= 1000000) {
    return `${sign}₹ ${fmt(absNum / 1000000)} M`;
  }
  if (absNum >= 1000) {
    return `${sign}₹ ${fmt(absNum / 1000)} K`;
  }
  return `${sign}₹ ${fmt(absNum)}`;
};

const renderCustomLegend = (props) => {
  const { payload } = props;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
      {payload.map((entry, index) => {
        if (entry.value === 'base' || entry.value === 'dummy') return null;
        let color = entry.color;
        if (entry.value === 'Over-Injection (OI)') color = '#ef4444';
        if (entry.value === 'Under-Injection (UI)') color = '#0288D1';
        const isLine = entry.type === 'line';
        return (
          <span key={`legend-${index}`} style={{ display: 'inline-flex', alignItems: 'center', marginRight: 10 }}>
            <span style={{
              display: 'inline-block',
              width: isLine ? 16 : 12,
              height: isLine ? 4 : 12,
              backgroundColor: color,
              marginRight: 6,
              borderRadius: isLine ? 0 : 2
            }} />
            <span style={{ fontSize: 12, color: '#555' }}>{entry.value}</span>
          </span>
        );
      })}
    </div>
  );
};

function KpiCardV2({ title, value, color }) {
  return (
    <Card size="small" style={{ background: color || '#f0f3ff', borderRadius: 6, padding: '4px 8px', border: '1px solid #e8e8e8', height: '100%' }} bodyStyle={{ padding: 0 }}>
      <div style={{ fontSize: 10, color: '#555', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#1B4F72', marginTop: 2, whiteSpace: 'nowrap' }}>
        {value}
      </div>
    </Card>
  );
}

const WaterfallTooltip = ({ active, payload, label, totalGross }) => {
  if (active && payload && payload.length) {
    const entry = payload[0].payload;

    const getPctString = (val) => {
      if (!totalGross || totalGross === 0) return '0%';
      const pct = (Math.abs(val) / totalGross) * 100;
      return pct === 0 ? '0%' : pct < 0.1 ? '<0.1%' : pct % 1 === 0 ? `${pct.toFixed(0)}%` : `${pct.toFixed(1)}%`;
    };

    if (entry.isGross) {
      return (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #d9d9d9',
          padding: '8px 12px',
          borderRadius: 4,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 12, fontWeight: 700, color: '#333', marginBottom: 6 }}>
            <span>Gross revenue:</span>
            <span>{formatValueKM(entry.raw, 2)} (100%)</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 12, color: '#555', marginBottom: 4, paddingLeft: 8 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: '#8E44AD' }} />
              Exchange:
            </span>
            <span style={{ fontWeight: 600 }}>{formatValueKM(entry.exchGross, 2)} ({getPctString(entry.exchGross)})</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 12, color: '#555', paddingLeft: 8 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: '#3498DB' }} />
              Bilateral:
            </span>
            <span style={{ fontWeight: 600 }}>{formatValueKM(entry.contractGross, 2)} ({getPctString(entry.contractGross)})</span>
          </div>
        </div>
      );
    }

    const color = entry.color || '#333';
    return (
      <div style={{
        backgroundColor: '#fff',
        border: '1px solid #d9d9d9',
        padding: '8px 12px',
        borderRadius: 4,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 12, color: '#555' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#333' }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: color }} />
            {label}:
          </span>
          <span style={{ fontWeight: 700, color: '#333' }}>{formatValueKM(entry.raw, 2)} ({getPctString(entry.raw)})</span>
        </div>
      </div>
    );
  }
  return null;
};

const WaterfallBarShape = (props) => {
  const { fill, x, y, width, height, index, payload } = props;

  const minBarHeight = 4;
  const renderHeight = Math.max(height, minBarHeight);

  if (payload.isGross) {
    const exchVal = payload.exchGross;
    const contractVal = payload.contractGross;
    const totalVal = payload.raw;

    const exchRatio = totalVal > 0 ? exchVal / totalVal : 0;
    const contractRatio = totalVal > 0 ? contractVal / totalVal : 0;

    const exchHeight = height * exchRatio;
    const contractHeight = height * contractRatio;

    // Segment rects: Exchange at bottom, Contracts at top (straight edges)
    const rectContract = (
      <rect
        x={x}
        y={y}
        width={width}
        height={contractHeight}
        fill="#3498DB"
      />
    );

    const rectExch = (
      <rect
        x={x}
        y={y + contractHeight}
        width={width}
        height={exchHeight}
        fill="#8E44AD"
      />
    );

    const showExchLabel = exchHeight >= 14 && exchVal > 0;
    const showContractLabel = contractHeight >= 14 && contractVal > 0;

    const labelExchText = formatTooltipValue(exchVal, 2);
    const labelContractText = formatTooltipValue(contractVal, 2);

    const labelExch = showExchLabel ? (
      <text
        x={x + width / 2}
        y={y + contractHeight + exchHeight / 2}
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={10}
        fontWeight={600}
      >
        {labelExchText}
      </text>
    ) : null;

    const labelContract = showContractLabel ? (
      <text
        x={x + width / 2}
        y={y + contractHeight / 2}
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={10}
        fontWeight={600}
      >
        {labelContractText}
      </text>
    ) : null;

    const bandwidth = props.xAxis?.bandwidth || (width * 2.5);
    const nextX = x + bandwidth;
    const connectorY = y;

    return (
      <g>
        {rectContract}
        {rectExch}
        {labelContract}
        {labelExch}
        <line
          x1={x + width}
          y1={connectorY}
          x2={nextX}
          y2={connectorY}
          stroke="#888"
          strokeWidth={1.5}
          strokeDasharray="3 3"
        />
      </g>
    );
  }

  // Non-gross columns: render single rect with straight edges
  const rect = (
    <rect
      x={x}
      y={y}
      width={width}
      height={renderHeight}
      fill={fill}
    />
  );

  const labelText = formatTooltipValue(payload.raw, 2);
  const isTiny = height < 18;
  const labelY = isTiny
    ? (payload.raw >= 0 ? y - 8 : y + renderHeight + 8)
    : y + height / 2;
  const labelColor = isTiny ? '#333' : '#fff';

  const label = Math.abs(payload.raw) > 0 ? (
    <text
      x={x + width / 2}
      y={labelY}
      fill={labelColor}
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize={10}
      fontWeight={600}
    >
      {labelText}
    </text>
  ) : null;

  if (index === 3) {
    return (
      <g>
        {rect}
        {label}
      </g>
    );
  }

  let connectorY = y;
  if (index === 1) {
    connectorY = y + height;
  } else if (index === 2) {
    const dsmVal = payload.raw;
    if (dsmVal < 0) {
      connectorY = y + height;
    }
  }

  const bandwidth = props.xAxis?.bandwidth || (width * 2.5);
  const nextX = x + bandwidth;

  return (
    <g>
      {rect}
      {label}
      <line
        x1={x + width}
        y1={connectorY}
        x2={nextX}
        y2={connectorY}
        stroke="#888"
        strokeWidth={1.5}
        strokeDasharray="3 3"
      />
    </g>
  );
};

const WaterfallPerUnitTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const entry = payload[0].payload;
    const color = entry.color || '#333';
    const isNetRecPay = entry.name?.toLowerCase().includes('rec/pay');
    const isCharges = entry.name === 'Charges';
    const isDsm = entry.name?.toLowerCase().includes('dsm') && entry.name !== 'Revenue post-DSM';
    const rawVal = Number(entry.raw) || 0;

    let signPrefix = '';
    let suffixTag = '';
    if (isNetRecPay) {
      signPrefix = rawVal >= 0 ? '+' : '-';
      suffixTag = rawVal >= 0 ? ' (Receivable)' : ' (Payable)';
    } else if (isCharges || isDsm) {
      signPrefix = '-';
    }

    return (
      <div style={{
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        padding: '8px 12px',
        borderRadius: 6,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        fontSize: 12,
        fontFamily: 'Inter, sans-serif'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: color }} />
          <span>{entry.name}{suffixTag}:</span>
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: color }}>
          {signPrefix}₹ {Math.abs(rawVal).toFixed(2)} / kWh
        </div>
      </div>
    );
  }
  return null;
};

const WaterfallPerUnitBarShape = (props) => {
  const { fill, x, y, width, height, index, payload } = props;
  const minBarHeight = 4;
  const renderHeight = Math.max(height, minBarHeight);

  const rect = (
    <rect
      x={x}
      y={y}
      width={width}
      height={renderHeight}
      fill={fill}
      rx={3}
    />
  );

  const rawVal = payload?.raw ?? 0;
  const isNetRecPay = payload?.name === 'Net Receivable/Payable';
  const prefix = isNetRecPay ? (rawVal >= 0 ? '+' : '-') : (rawVal < 0 ? '-' : '');
  const labelText = `${prefix}₹${Math.abs(rawVal).toFixed(2)}`;
  const isTiny = height < 18;
  const labelY = isTiny
    ? (rawVal >= 0 ? y - 8 : y + renderHeight + 8)
    : y + height / 2;
  const labelColor = isTiny ? '#333' : '#fff';

  const label = Math.abs(rawVal) > 0 ? (
    <text
      x={x + width / 2}
      y={labelY}
      fill={labelColor}
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize={10.5}
      fontWeight={700}
    >
      {labelText}
    </text>
  ) : null;

  const isFinalBar = index === 4 || payload?.name === 'Revenue post-DSM';
  if (isFinalBar) {
    return (
      <g>
        {rect}
        {label}
      </g>
    );
  }

  let connectorY = y;
  if (index === 0) {
    connectorY = y;
  } else {
    connectorY = rawVal < 0 ? y + height : y;
  }

  const bandwidth = props.xAxis?.bandwidth || (width * 2.2);
  const nextX = x + bandwidth;

  return (
    <g>
      {rect}
      {label}
      <line
        x1={x + width}
        y1={connectorY}
        x2={nextX}
        y2={connectorY}
        stroke="#94a3b8"
        strokeWidth={1.5}
        strokeDasharray="3 3"
      />
    </g>
  );
};

function PerUnitChargesDonut({ data, colors }) {
  const total = useMemo(() => {
    return data.reduce((acc, curr) => acc + Math.abs(curr.value || 0), 0);
  }, [data]);

  const pieChartData = useMemo(() => {
    return data.map(item => ({
      ...item,
      displayValue: Math.abs(item.value || 0)
    }));
  }, [data]);

  const sortedLegends = useMemo(() => {
    return [...data].sort((a, b) => Math.abs(b.value || 0) - Math.abs(a.value || 0));
  }, [data]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const entry = payload[0].payload;
      const val = Math.abs(entry.value || 0);
      const pct = total > 0 ? (val / total) * 100 : 0;
      return (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #e2e8f0',
          padding: '8px 12px',
          borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          fontSize: 12,
          zIndex: 99999,
          position: 'relative'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: colors[entry.key] || '#8884d8' }} />
            <span>{entry.name}</span>
          </div>
          <div style={{ fontWeight: 700, color: '#0f172a' }}>
            ₹ {val.toFixed(2)} / kWh ({pct.toFixed(1)}%)
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', gap: 16 }}>
      <div style={{ position: 'relative', width: 170, height: 170, flexShrink: 0 }}>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          pointerEvents: 'none',
          zIndex: 1
        }}>
          <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Total Deductions</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>
            ₹ {total.toFixed(2)} / kWh
          </div>
        </div>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieChartData}
              dataKey="displayValue"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={48}
              outerRadius={74}
              paddingAngle={2}
            >
              {pieChartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[entry.key] || '#8884d8'} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 99999, pointerEvents: 'none' }} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', maxHeight: 200, paddingRight: 4 }}>
        {sortedLegends.map((entry) => {
          const val = Math.abs(entry.value || 0);
          const pct = total > 0 ? (val / total) * 100 : 0;

          return (
            <div key={entry.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, paddingBottom: 4, borderBottom: '1px solid #f8fafc' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: colors[entry.key] || '#888', flexShrink: 0 }} />
                <span style={{ color: '#334155', fontWeight: 500 }}>{entry.name}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ color: '#0f172a', fontWeight: 700, fontSize: 11 }}>
                  ₹ {val.toFixed(2)} / kWh
                </span>
                <span style={{ color: '#64748b', fontSize: 11, minWidth: 32, textAlign: 'right' }}>
                  {pct.toFixed(0)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DonutChartWithLegend({ data, colors, title, segmentRates, percentageBaseValue, totalVolume }) {
  const { exchNetRev, exchVolume, contractNetRev, contractVolume } = segmentRates || { exchNetRev: 0, exchVolume: 0, contractNetRev: 0, contractVolume: 0 };

  const [isExchangeExpanded, setIsExchangeExpanded] = useState(false);

  const exchRate = exchVolume > 0 ? exchNetRev / exchVolume : 0;
  const contractRate = contractVolume > 0 ? contractNetRev / contractVolume : 0;
  const combinedRate = (exchVolume + contractVolume) > 0 ? (exchNetRev + contractNetRev) / (exchVolume + contractVolume) : 0;

  const total = useMemo(() => {
    return data.reduce((acc, curr) => acc + Math.abs(curr.value || 0), 0);
  }, [data]);

  const netTotal = useMemo(() => {
    return data.reduce((acc, curr) => acc + (curr.value || 0), 0);
  }, [data]);

  const pieChartData = useMemo(() => {
    return data.map(item => ({
      ...item,
      displayValue: Math.abs(item.value || 0)
    }));
  }, [data]);

  const sortedLegends = useMemo(() => {
    return [...data].sort((a, b) => Math.abs(b.value || 0) - Math.abs(a.value || 0));
  }, [data]);

  const formatCurrency = (val) => {
    const absVal = Math.abs(val);
    const sign = val < 0 ? '-' : '';
    if (absVal >= 1000000) {
      return `${sign}₹ ${(absVal / 1000000).toFixed(2)} M`;
    }
    if (absVal >= 1000) {
      return `${sign}₹ ${(absVal / 1000).toFixed(2)} K`;
    }
    return `${sign}₹ ${absVal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const entry = payload[0].payload;
      const val = Math.abs(entry.value || 0);
      const pctBase = percentageBaseValue !== undefined && percentageBaseValue !== null ? Math.abs(percentageBaseValue) : total;
      const pct = pctBase > 0 ? (val / pctBase) * 100 : 0;
      const pctString = pct === 0 ? '0%' : pct < 0.1 ? '<0.1%' : pct % 1 === 0 ? `${pct.toFixed(0)}%` : `${pct.toFixed(1)}%`;
      const formattedVal = formatCurrency(entry.value);

      return (
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          border: '1px solid #e0e0e0',
          padding: '8px 12px',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          fontSize: '12px',
          color: '#333'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, marginBottom: 4 }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: colors[entry.key] || '#8884d8' }} />
            <span>{entry.name}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
            <span>Net Revenue:</span>
            <span style={{ fontWeight: 600 }}>{formattedVal} ({pctString})</span>
          </div>
          {entry.breakdown && (
            <div style={{ borderTop: '1px solid #eaeaea', paddingTop: 6, marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* GDAM */}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '11px', color: '#555' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#2ECC71' }} />
                  <span>GDAM</span>
                </div>
                <span style={{ fontWeight: 500 }}>
                  {formatCurrency(entry.breakdown.gdam)} ({entry.value > 0 ? `${((entry.breakdown.gdam / entry.value) * 100).toFixed(1)}%` : '0%'})
                </span>
              </div>
              {/* DAM */}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '11px', color: '#555' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#E67E22' }} />
                  <span>DAM</span>
                </div>
                <span style={{ fontWeight: 500 }}>
                  {formatCurrency(entry.breakdown.dam)} ({entry.value > 0 ? `${((entry.breakdown.dam / entry.value) * 100).toFixed(1)}%` : '0%'})
                </span>
              </div>
              {/* RTM */}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: '11px', color: '#555' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#3498DB' }} />
                  <span>RTM</span>
                </div>
                <span style={{ fontWeight: 500 }}>
                  {formatCurrency(entry.breakdown.rtm)} ({entry.value > 0 ? `${((entry.breakdown.rtm / entry.value) * 100).toFixed(1)}%` : '0%'})
                </span>
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 32, padding: '0 16px', flexWrap: 'wrap' }}>
      {/* Donut Chart Container (takes 50% width on side-by-side layout) */}
      <div style={{ position: 'relative', width: '260px', height: 260, flex: '1 1 calc(50% - 16px)', minWidth: '220px' }}>
        {/* Centered label for Donut */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          pointerEvents: 'none',
          zIndex: 1
        }}>
          <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>Total</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111', marginTop: 2 }}>
            {formatCurrency(netTotal)}
          </div>
          {percentageBaseValue !== undefined && percentageBaseValue !== null && (() => {
            const pct = percentageBaseValue > 0 ? (netTotal / percentageBaseValue) * 100 : 0;
            const pctString = pct === 0 ? '0%' : pct < 0.1 ? '<0.1%' : pct % 1 === 0 ? `${pct.toFixed(0)}%` : `${pct.toFixed(1)}%`;
            return (
              <div style={{ fontSize: 11, color: '#666', marginTop: 2, fontWeight: 500 }}>
                ({pctString})
              </div>
            );
          })()}
        </div>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieChartData}
              dataKey="displayValue"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={65}
              outerRadius={95}
              paddingAngle={2}
            >
              {pieChartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[entry.key] || '#8884d8'} />
              ))}
            </Pie>
            <Tooltip
              content={<CustomTooltip />}
              wrapperStyle={{ zIndex: 1000 }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Legend Container (takes 50% width on side-by-side layout, listed vertically in descending order) */}
      <div style={{ flex: '1 1 calc(50% - 16px)', minWidth: '220px', display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto', paddingRight: 8 }}>
        {sortedLegends.map((entry) => {
          const val = Math.abs(entry.value || 0);
          const pctBase = percentageBaseValue !== undefined && percentageBaseValue !== null ? Math.abs(percentageBaseValue) : total;
          const pct = pctBase > 0 ? (val / pctBase) * 100 : 0;
          const pctString = pct === 0 ? '0%' : pct < 0.1 ? '<0.1%' : pct % 1 === 0 ? `${pct.toFixed(0)}%` : `${pct.toFixed(1)}%`;
          const formattedVal = formatCurrency(entry.value);

          const rateVal = totalVolume > 0 ? (val / totalVolume) : 0;

          return (
            <div key={entry.key} style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, paddingBottom: 4, paddingTop: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: colors[entry.key], flexShrink: 0 }} />
                  <span style={{ color: '#333', fontWeight: 500 }}>{entry.name}</span>
                  {entry.breakdown && (
                    <Button
                      type="text"
                      size="small"
                      style={{ padding: '0 4px', height: '18px', fontSize: 10, display: 'inline-flex', alignItems: 'center', minWidth: 0, lineHeight: 1 }}
                      icon={isExchangeExpanded ? <MinusOutlined style={{ fontSize: 9 }} /> : <PlusOutlined style={{ fontSize: 9 }} />}
                      onClick={() => setIsExchangeExpanded(!isExchangeExpanded)}
                    />
                  )}
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0 }}>
                  {totalVolume > 0 && (
                    <span style={{ color: '#0052CC', fontWeight: 700, fontSize: 11, marginRight: 8 }}>
                      {rateVal.toFixed(2)} ₹/Unit
                    </span>
                  )}
                  <span style={{ color: '#777', fontSize: 11 }}>{formattedVal}</span>
                  <span style={{ color: '#111', fontWeight: 600, minWidth: 40, textAlign: 'right' }}>{pctString}</span>
                </div>
              </div>

              {entry.breakdown && isExchangeExpanded && (
                <div style={{ paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 4, paddingBottom: 6 }}>
                  {/* GDAM */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#666' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#2ECC71' }} />
                      <span>GDAM</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <span>{formatCurrency(entry.breakdown.gdam)}</span>
                      <span style={{ minWidth: 40, textAlign: 'right', fontWeight: 600, color: '#111' }}>
                        {entry.value > 0 ? `${((entry.breakdown.gdam / entry.value) * 100).toFixed(1)}%` : '0%'}
                      </span>
                    </div>
                  </div>
                  {/* DAM */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#666' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#E67E22' }} />
                      <span>DAM</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <span>{formatCurrency(entry.breakdown.dam)}</span>
                      <span style={{ minWidth: 40, textAlign: 'right', fontWeight: 600, color: '#111' }}>
                        {entry.value > 0 ? `${((entry.breakdown.dam / entry.value) * 100).toFixed(1)}%` : '0%'}
                      </span>
                    </div>
                  </div>
                  {/* RTM */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#666' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#3498DB' }} />
                      <span>RTM</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <span>{formatCurrency(entry.breakdown.rtm)}</span>
                      <span style={{ minWidth: 40, textAlign: 'right', fontWeight: 600, color: '#111' }}>
                        {entry.value > 0 ? `${((entry.breakdown.rtm / entry.value) * 100).toFixed(1)}%` : '0%'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Combined Summary Section */}
        {(exchVolume > 0 || contractVolume > 0) && <div style={{ borderTop: '1px solid #ddd', margin: '4px 0' }} />}
        {exchVolume > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#555', padding: '2px 0' }}>
            <span style={{ fontWeight: 600 }}>Net Tariff (Exchange)</span>
            <span style={{ color: '#0052CC', fontWeight: 700 }}>{exchRate.toFixed(2)} ₹/kWh</span>
          </div>
        )}
        {contractVolume > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#555', padding: '2px 0' }}>
            <span style={{ fontWeight: 600 }}>Net Tariff (All Bilaterals)</span>
            <span style={{ color: '#0052CC', fontWeight: 700 }}>{contractRate.toFixed(2)} ₹/kWh</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Border and Header ribbon styling for boxed containers and graphs in Revenue Summary tab.
// Change these constants to update border and header styling across all Revenue Summary boxes!
const SUMMARY_BOX_BORDER = '1px solid #94a3b8';
const SUMMARY_CARD_HEAD_STYLE = {
  background: '#f1f5f9',
  borderBottom: '1px solid #cbd5e1',
  padding: '0 20px',
  minHeight: 44,
  borderTopLeftRadius: 7,
  borderTopRightRadius: 7
};

function ContractTimelineChart({ plantId }) {
  const [hoveredContract, setHoveredContract] = useState(null);
  const [apiContracts, setApiContracts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Timeline starting from April 2025 (Apr-25)
  const timelineStart = dayjs('2025-04-01');
  const totalMonths = 21; // Apr 2025 to Dec 2026

  const months = useMemo(() => {
    const list = [];
    for (let i = 0; i < totalMonths; i++) {
      list.push(timelineStart.add(i, 'month').format('MMM-YY'));
    }
    return list;
  }, [timelineStart]);

  const windowStartDate = timelineStart.startOf('month');
  const windowEndDate = timelineStart.add(totalMonths - 1, 'month').endOf('month');

  // Helper to map date string to index in months array (clamped between 0 and months.length - 1)
  const getMonthIndex = (dateStr) => {
    if (!dateStr) return 0;
    const d = dayjs(dateStr);
    if (!d.isValid()) return 0;
    const diffMonths = (d.year() - timelineStart.year()) * 12 + (d.month() - timelineStart.month());
    return Math.max(0, Math.min(months.length - 1, diffMonths));
  };

  const palette = ['#2563eb', '#d97706', '#0d9488', '#e11d48', '#0284c7', '#16a34a', '#7c3aed'];

  useEffect(() => {
    let isMounted = true;
    const fetchContractsData = async () => {
      setLoading(true);
      try {
        const params = {};
        if (plantId) params.plant = plantId;
        const res = await getContracts(params);
        const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        if (isMounted && data.length > 0) {
          const validContracts = data.filter((item) => {
            const cStart = item.contract_start_date ? dayjs(item.contract_start_date) : null;
            const cEnd = item.contract_end_date ? dayjs(item.contract_end_date) : null;
            if (!cStart && !cEnd) return true;
            if (cEnd && cEnd.isBefore(windowStartDate)) return false;
            if (cStart && cStart.isAfter(windowEndDate)) return false;
            return true;
          });

          const mapped = validContracts.map((item, idx) => {
            const startIdx = getMonthIndex(item.contract_start_date);
            const endIdx = getMonthIndex(item.contract_end_date);
            const startMonth = item.contract_start_date ? dayjs(item.contract_start_date).format('MMM-YYYY') : timelineStart.format('MMM-YYYY');
            const endMonth = item.contract_end_date ? dayjs(item.contract_end_date).format('MMM-YYYY') : windowEndDate.format('MMM-YYYY');
            const rateVal = parseFloat(item.contract_rate_kwh) || 4.0;
            const mwVal = parseFloat(item.contract_quantum_mw) || 0;
            const entityName = item.entity || `Contract #${item.id}`;
            const cType = item.contract_type ? ` (${item.contract_type})` : '';

            return {
              id: String(item.id),
              name: `${entityName}${cType}`,
              counterparty: item.entity || 'Bilateral Counterparty',
              rate: rateVal,
              mwVal: mwVal,
              startIdx: Math.min(startIdx, endIdx),
              endIdx: Math.max(startIdx, endIdx),
              startMonth,
              endMonth,
              capacity: `${mwVal.toFixed(1)} MW`,
              color: palette[idx % palette.length],
            };
          });

          setApiContracts(mapped);
        }
      } catch (err) {
        console.warn('Could not fetch contracts dynamically from API, using default portfolio:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchContractsData();
    return () => { isMounted = false; };
  }, [plantId]);

  const defaultContracts = [
    {
      id: 'goa',
      name: 'Goa Long-Term PPA (Non-RTC)',
      counterparty: 'Goa',
      rate: 3.95,
      mwVal: 50.4,
      startIdx: getMonthIndex('2026-04-01'),
      endIdx: getMonthIndex('2026-09-30'),
      startMonth: 'Apr-2026',
      endMonth: 'Sep-2026',
      capacity: '50.4 MW',
      color: '#2563eb',
    },
    {
      id: 'nidar',
      name: 'Nidar Medium-Term PPA (RTC)',
      counterparty: 'Nidar',
      rate: 4.48,
      mwVal: 3.0,
      startIdx: getMonthIndex('2026-04-01'),
      endIdx: getMonthIndex('2026-12-31'),
      startMonth: 'Apr-2026',
      endMonth: 'Dec-2026',
      capacity: '3.0 MW',
      color: '#d97706',
    },
    {
      id: 'npcl',
      name: 'NPCL Bilateral Contract (RTC)',
      counterparty: 'NPCL',
      rate: 4.95,
      mwVal: 6.0,
      startIdx: getMonthIndex('2026-04-01'),
      endIdx: getMonthIndex('2026-12-31'),
      startMonth: 'Apr-2026',
      endMonth: 'Dec-2026',
      capacity: '6.0 MW',
      color: '#0d9488',
    },
    {
      id: 'goa_ged',
      name: 'Goa (GED) Short-Term (Non-RTC)',
      counterparty: 'Goa (GED)',
      rate: 3.95,
      mwVal: 50.4,
      startIdx: getMonthIndex('2025-11-01'),
      endIdx: getMonthIndex('2026-03-31'),
      startMonth: 'Nov-2025',
      endMonth: 'Mar-2026',
      capacity: '50.4 MW',
      color: '#e11d48',
    },
  ];

  const rawContracts = apiContracts.length > 0 ? apiContracts : defaultContracts;

  // Collision detection: group contracts that overlap in time and rate
  // If contracts overlap, show only one on the y-axis (exact rate), with a label "+N" above it,
  // and on hover display all overlapping contracts in the tooltip.
  const contractGroups = useMemo(() => {
    if (!rawContracts || rawContracts.length === 0) return [];

    // Group into clusters of identical/close rate & overlapping period
    const clusters = [];
    rawContracts.forEach((c) => {
      let matchedCluster = null;
      for (const cluster of clusters) {
        const hasCollision = cluster.some((other) => {
          const timeOverlap = !(c.endIdx < other.startIdx || c.startIdx > other.endIdx);
          const rateOverlap = Math.abs(c.rate - other.rate) < 0.05; // identical or virtually same tariff
          return timeOverlap && rateOverlap;
        });
        if (hasCollision) {
          matchedCluster = cluster;
          break;
        }
      }
      if (matchedCluster) {
        matchedCluster.push(c);
      } else {
        clusters.push([c]);
      }
    });

    return clusters.map((cluster, cIdx) => {
      const isOverlapping = cluster.length > 1;
      const primary = cluster[0];
      const startIdx = Math.min(...cluster.map(c => c.startIdx));
      const endIdx = Math.max(...cluster.map(c => c.endIdx));
      const rate = primary.rate;
      const totalMw = cluster.reduce((sum, c) => sum + (c.mwVal || 0), 0);

      return {
        groupId: `group-${cIdx}-${primary.id}`,
        contracts: cluster,
        primaryContract: primary,
        isOverlapping,
        overlapCount: cluster.length,
        rate,
        startIdx,
        endIdx,
        totalMw,
        color: primary.color,
      };
    });
  }, [rawContracts]);

  // Calculate line thickness based on contract capacity (higher MW = thicker line)
  const getStrokeWidth = (grp, isHovered) => {
    const mw = grp.totalMw || grp.primaryContract.mwVal || parseFloat(grp.primaryContract.capacity) || 5;
    const baseWidth = Math.max(4, Math.min(14, 4 + (mw / 50.0) * 8.0));
    return isHovered ? baseWidth + 2.5 : baseWidth;
  };

  // Dynamically calculate rate range with margin
  const contractRates = contractGroups.map(g => g.rate);
  const minObserved = Math.min(...contractRates, 3.50);
  const maxObserved = Math.max(...contractRates, 5.50);

  const minRate = Math.max(0, Math.floor(minObserved - 0.5));
  const maxRate = Math.ceil(maxObserved + 0.5);
  const svgWidth = 920;
  const svgHeight = 250;
  const margin = { top: 38, right: 30, bottom: 65, left: 65 };
  const chartW = svgWidth - margin.left - margin.right;
  const chartH = svgHeight - margin.top - margin.bottom;

  const getX = (idx) => margin.left + (idx / (months.length - 1)) * chartW;
  const getY = (rate) => margin.top + chartH - ((rate - minRate) / ((maxRate - minRate) || 1)) * chartH;

  const rateTicks = [];
  for (let r = minRate; r <= maxRate; r += 1) {
    rateTicks.push(Math.round(r));
  }

  return (
    <Card
      bordered={true}
      headStyle={SUMMARY_CARD_HEAD_STYLE}
      title={<span style={{ fontSize: 15, fontWeight: 700, color: '#1B4F72' }}>Contracts Portfolio</span>}
      style={{
        marginBottom: 20,
        borderRadius: 8,
        background: '#fff',
        border: SUMMARY_BOX_BORDER,
        overflow: 'visible'
      }}
      bodyStyle={{ padding: '20px 24px', overflow: 'visible' }}
    >
      <div style={{ width: '100%', position: 'relative', overflow: 'visible' }}>
        <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} style={{ width: '100%', minWidth: 720, height: 'auto' }}>
          {/* Horizontal Grid Lines */}
          {rateTicks.map((rt) => {
            const yPos = getY(rt);
            return (
              <g key={rt}>
                <line
                  x1={margin.left}
                  y1={yPos}
                  x2={svgWidth - margin.right}
                  y2={yPos}
                  stroke="#e2e8f0"
                  strokeDasharray="4 4"
                />
                <text
                  x={margin.left - 10}
                  y={yPos + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill="#64748b"
                  fontWeight="500"
                >
                  ₹{rt}
                </text>
              </g>
            );
          })}

          {/* Vertical Month Grid Lines & Slanted Labels */}
          {months.map((m, idx) => {
            const xPos = getX(idx);
            const yPos = margin.top + chartH;
            return (
              <g key={m}>
                <line
                  x1={xPos}
                  y1={margin.top}
                  x2={xPos}
                  y2={yPos}
                  stroke="#f1f5f9"
                />
                <text
                  x={xPos}
                  y={yPos + 8}
                  textAnchor="end"
                  transform={`rotate(-45, ${xPos}, ${yPos + 8})`}
                  fontSize="9.5"
                  fill="#64748b"
                  fontWeight="500"
                >
                  {m}
                </text>
              </g>
            );
          })}

          {/* Axis Labels */}
          <text
            x={14}
            y={margin.top - 14}
            textAnchor="start"
            fontSize="10"
            fill="#94a3b8"
            fontWeight="700"
          >
            Tariff (₹/kWh)
          </text>
          <text
            x={margin.left + chartW / 2}
            y={svgHeight - 6}
            textAnchor="middle"
            fontSize="10"
            fill="#94a3b8"
            fontWeight="700"
          >
            Month / Year
          </text>

          {/* Contract Lines & Bars */}
          {contractGroups.map((grp) => {
            const rawX1 = getX(grp.startIdx);
            const rawX2 = getX(grp.endIdx);
            const isSingleMonth = Math.abs(rawX2 - rawX1) < 4;
            const minSpan = 14;
            const x1 = isSingleMonth ? rawX1 - minSpan : rawX1;
            const x2 = isSingleMonth ? rawX2 + minSpan : rawX2;
            const yPos = getY(grp.rate);
            const isHovered = hoveredContract?.groupId === grp.groupId;
            const strokeW = getStrokeWidth(grp, isHovered);
            const radius = Math.max(4, strokeW / 2 + 1.5);
            const midX = (x1 + x2) / 2;

            return (
              <g
                key={grp.groupId}
                onMouseEnter={() => setHoveredContract({ ...grp, visualX1: x1, visualX2: x2, visualY: yPos })}
                onMouseLeave={() => setHoveredContract(null)}
                style={{ cursor: 'pointer' }}
              >
                {/* Invisible wider hit area for easy hover */}
                <line
                  x1={x1 - 6}
                  y1={yPos}
                  x2={x2 + 6}
                  y2={yPos}
                  stroke="transparent"
                  strokeWidth={Math.max(20, strokeW + 12)}
                />

                {/* Overlapping Contracts Label above the line: just +N */}
                {grp.isOverlapping && (
                  <g>
                    <rect
                      x={midX - 9}
                      y={yPos - strokeW / 2 - 11}
                      width={18}
                      height={9}
                      rx={4.5}
                      fill="#f0f9ff"
                      stroke="#7dd3fc"
                      strokeWidth={0.6}
                    />
                    <text
                      x={midX}
                      y={yPos - strokeW / 2 - 4}
                      textAnchor="middle"
                      fontSize="7"
                      fontWeight="700"
                      fill="#0284c7"
                    >
                      +{grp.overlapCount}
                    </text>
                  </g>
                )}

                {/* Main span line (single line placed accurately on the Y-axis rate) */}
                <line
                  x1={x1}
                  y1={yPos}
                  x2={x2}
                  y2={yPos}
                  stroke={grp.color}
                  strokeWidth={strokeW}
                  strokeLinecap="round"
                />

                {/* Start Endpoint Circle */}
                <circle
                  cx={x1}
                  cy={yPos}
                  r={radius}
                  fill="#fff"
                  stroke={grp.color}
                  strokeWidth={2.5}
                />

                {/* End Endpoint Circle */}
                <circle
                  cx={x2}
                  cy={yPos}
                  r={radius}
                  fill={grp.color}
                  stroke="#fff"
                  strokeWidth={1.5}
                />
              </g>
            );
          })}
        </svg>

        {/* Hover Tooltip Overlay */}
        {hoveredContract && (
          <div
            style={{
              position: 'absolute',
              top: Math.max(10, (hoveredContract.visualY ?? getY(hoveredContract.rate)) - 14),
              left: ((hoveredContract.visualX1 ?? getX(hoveredContract.startIdx)) + (hoveredContract.visualX2 ?? getX(hoveredContract.endIdx))) / 2,
              transform: (hoveredContract.visualY ?? getY(hoveredContract.rate)) < 80 ? 'translate(-50%, 14px)' : 'translate(-50%, -100%)',
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              color: '#0f172a',
              padding: '10px 14px',
              borderRadius: 8,
              boxShadow: '0 20px 30px -5px rgba(15, 23, 42, 0.3), 0 10px 15px -5px rgba(15, 23, 42, 0.2)',
              fontSize: 11,
              zIndex: 999999,
              pointerEvents: 'none',
              minWidth: 220,
              maxWidth: 320,
            }}
          >
            {hoveredContract.isOverlapping ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', paddingBottom: 6, marginBottom: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 12, color: '#0369a1' }}>
                    ✦ {hoveredContract.overlapCount} Overlapping Contracts
                  </span>
                  <span style={{ fontWeight: 700, color: '#0284c7', fontSize: 12 }}>
                    ₹{hoveredContract.rate.toFixed(2)} / kWh
                  </span>
                </div>
                {hoveredContract.contracts.map((c, idx) => (
                  <div key={c.id || idx} style={{ marginBottom: idx < hoveredContract.contracts.length - 1 ? 8 : 0, paddingLeft: 8, borderLeft: `3px solid ${c.color}` }}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: c.color, marginBottom: 2 }}>
                      {c.name}
                    </div>
                    <div style={{ color: '#475569', fontSize: 10.5 }}>Validity: <strong style={{ color: '#0f172a' }}>{c.startMonth} — {c.endMonth}</strong></div>
                    <div style={{ color: '#475569', fontSize: 10.5 }}>Capacity: <strong style={{ color: '#0f172a' }}>{c.capacity}</strong></div>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4, color: hoveredContract.primaryContract.color }}>
                  {hoveredContract.primaryContract.name}
                </div>
                <div style={{ color: '#475569', marginBottom: 2 }}>Tariff Rate: <strong style={{ color: '#0284c7' }}>₹{hoveredContract.rate.toFixed(2)} / kWh</strong></div>
                <div style={{ color: '#475569', marginBottom: 2 }}>Validity: <strong style={{ color: '#0f172a' }}>{hoveredContract.primaryContract.startMonth} — {hoveredContract.primaryContract.endMonth}</strong></div>
                <div style={{ color: '#475569' }}>Capacity: <strong style={{ color: '#0f172a' }}>{hoveredContract.primaryContract.capacity}</strong></div>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid #f1f5f9', display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 11, color: '#64748b', alignItems: 'center' }}>
        {rawContracts.map((c) => (
          <span key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: c.color }} />
            <span>{c.counterparty || c.name} ({c.capacity})</span>
          </span>
        ))}
        {loading && <Spin size="small" style={{ marginLeft: 8 }} />}
      </div>
    </Card>
  );
}

function SalesMixPieItem({ title, data }) {
  const { totalMU, bilatPct, exchPct, gdamPct, damPct, rtmPct, bilatVol, exchVol, gdamVol, damVol, rtmVol } = data || {};

  const bVal = bilatVol || (bilatPct * 10) || 1;
  const eVal = exchVol || (exchPct * 10) || 1;
  const gVal = gdamVol || (gdamPct * 10) || 1;
  const dVal = damVol || (damPct * 10) || 1;
  const rVal = rtmVol || (rtmPct * 10) || 1;

  const innerData = [
    { name: 'Bilateral', value: bVal, fill: '#5B9BD5' },
    { name: 'Exchange', value: eVal, fill: '#418AB3' },
  ];

  const outerData = [
    { name: 'Bilateral (Spacer)', value: bVal, fill: 'transparent' },
    { name: 'GDAM', value: gVal, fill: '#48C9B0' },
    { name: 'DAM', value: dVal, fill: '#B2B7F2' },
    { name: 'RTM', value: rVal, fill: '#F8A51D' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ width: 210, height: 165, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <Pie
              data={innerData}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius={0}
              outerRadius={60}
              startAngle={90}
              endAngle={-270}
              stroke="#fff"
              strokeWidth={1.5}
              isAnimationActive={false}
            />
            <Pie
              data={outerData}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius={64}
              outerRadius={78}
              startAngle={90}
              endAngle={-270}
              stroke="#fff"
              strokeWidth={1}
              isAnimationActive={false}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div style={{ textAlign: 'center', marginTop: 2 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
          {title} · {totalMU} MU
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, fontWeight: 500 }}>
          Bilateral {bilatPct}% · Exch {exchPct}%
        </div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 1, fontWeight: 500 }}>
          GDAM {gdamPct}% · DAM {damPct}% · RTM {rtmPct}%
        </div>
      </div>
    </div>
  );
}

function RevenueSummaryTab({ apiParamsBase }) {
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState({
    mtd: null,
    ytd: null,
    std: null,
  });
  const [genPlfData, setGenPlfData] = useState({
    yesterdayLabel: dayjs().subtract(1, 'day').format('DD-MMM-YYYY'),
    yesterdayGen: '-',
    yesterdayPlf: '-',
    mtdGen: '-',
    mtdPlf: '-',
    ytdGen: '-',
    ytdPlf: '-',
    threeYearAvgGen: '-',
    threeYearAvgPlf: '-',
  });
  const [salesMixData, setSalesMixData] = useState({
    yesterday: { totalMU: '0.00', bilatPct: 0, exchPct: 0, gdamPct: 0, damPct: 0, rtmPct: 0 },
    mtd: { totalMU: '0.00', bilatPct: 0, exchPct: 0, gdamPct: 0, damPct: 0, rtmPct: 0 },
    ytd: { totalMU: '0.00', bilatPct: 0, exchPct: 0, gdamPct: 0, damPct: 0, rtmPct: 0 },
  });
  const emptyRevMix = {
    rev: {
      bilat: '-',
      exch: '-',
      rec: '-',
      recRaw: 0,
      totalGross: '-',
      netRecPayable: '-',
      netRecPayableRaw: 0,
      charges: '-',
      chargesRaw: 0,
      dsm: '-',
      dsmRaw: 0,
      netPostDsm: '-'
    },
    pu: {
      bilat: '-',
      exch: '-',
      rec: '-',
      recRaw: 0,
      totalGross: '-',
      netRecPayable: '-',
      netRecPayableRaw: 0,
      charges: '-',
      chargesRaw: 0,
      dsm: '-',
      dsmRaw: 0,
      netPostDsm: '-'
    }
  };

  const [revenueMixData, setRevenueMixData] = useState({
    yesterday: emptyRevMix,
    mtd: emptyRevMix,
    ytd: emptyRevMix,
  });

  const [ytdWaterfallData, setYtdWaterfallData] = useState([]);

  const [ytdChargesData, setYtdChargesData] = useState([]);

  const [chart5Data, setChart5Data] = useState([]);
  const [chartPlantPu3YData, setChartPlantPu3YData] = useState([]);
  const [chartExchPu3YData, setChartExchPu3YData] = useState([]);
  const [chart5dData, setChart5dData] = useState([]);
  const [chart30dExchVolData, setChart30dExchVolData] = useState([]);
  const [chartYestExchBlockVolData, setChartYestExchBlockVolData] = useState([]);
  const [chartYtdRecData, setChartYtdRecData] = useState([]);

  const [view5a, setView5a] = useState('chart');
  const [expandedChart5a, setExpandedChart5a] = useState(false);

  const [view5b, setView5b] = useState('chart');
  const [expandedChart5b, setExpandedChart5b] = useState(false);

  const [view5c, setView5c] = useState('chart');
  const [expandedChart5c, setExpandedChart5c] = useState(false);

  const [view5d, setView5d] = useState('chart');
  const [expandedChart5d, setExpandedChart5d] = useState(false);

  const [view30dExchVol, setView30dExchVol] = useState('chart');
  const [expandedChart30dExchVol, setExpandedChart30dExchVol] = useState(false);

  const [viewYestExchBlock, setViewYestExchBlock] = useState('chart');
  const [expandedChartYestExchBlock, setExpandedChartYestExchBlock] = useState(false);

  const [viewYtdRec, setViewYtdRec] = useState('chart');
  const [expandedChartYtdRec, setExpandedChartYtdRec] = useState(false);

  const apiParamsString = JSON.stringify(apiParamsBase);

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const today = dayjs();
        const yesterday = today.subtract(1, 'day');
        const yesterdayStr = yesterday.format('YYYY-MM-DD');
        const yesterdayDateLabel = yesterday.format('DD-MMM-YYYY');
        const parsedBase = JSON.parse(apiParamsString);

        const paramsYesterday = { ...parsedBase, date_from: yesterdayStr, date_to: yesterdayStr, granularity: 'daily' };
        const paramsMTD = { ...parsedBase, date_from: today.startOf('month').format('YYYY-MM-DD'), date_to: yesterdayStr, granularity: 'daily' };
        const paramsYTD = { ...parsedBase, date_from: today.startOf('year').format('YYYY-MM-DD'), date_to: yesterdayStr, granularity: 'monthly' };
        const paramsSTD = { ...parsedBase, date_from: '2026-04-01', date_to: yesterdayStr, granularity: 'monthly' };
        const startYear3Y = today.year() - 2;
        const params3Y = { ...parsedBase, date_from: `${startYear3Y}-01-01`, date_to: yesterdayStr, granularity: 'monthly' };
        const params30D = { ...parsedBase, date_from: today.subtract(30, 'day').format('YYYY-MM-DD'), date_to: yesterdayStr, granularity: 'daily' };
        const paramsYest15Min = { ...parsedBase, date_from: yesterdayStr, date_to: yesterdayStr, granularity: '15min' };

        const [resYest, resMTD, resYTD, resSTD, res3Y, res30D, resYestMarket15M] = await Promise.all([
          getRevenueAnalysis(paramsYesterday).catch(() => null),
          getRevenueAnalysis(paramsMTD).catch(() => null),
          getRevenueAnalysis(paramsYTD).catch(() => null),
          getRevenueAnalysis(paramsSTD).catch(() => null),
          getRevenueAnalysis(params3Y).catch(() => null),
          getRevenueAnalysis(params30D).catch(() => null),
          getMarketDashboard(paramsYest15Min).catch(() => null)
        ]);

        const processResults = (resultsRes) => {
          const results = resultsRes?.data?.results || resultsRes || [];
          let totalGross = 0, totalNetPre = 0, totalNetPost = 0, totalCharges = 0, totalScheduleMw = 0, totalGenerationMw = 0;
          (results || []).forEach(r => {
            totalGross += Number(r.gross_rev) || 0;
            totalNetPre += Number(r.net_pre) || 0;
            totalNetPost += Number(r.net_post) || 0;
            totalCharges += Number(r.total_charges) || 0;
            totalScheduleMw += Number(r.total_schedule_mw) || 0;
            totalGenerationMw += Number(r.total_generation_mw) || 0;
          });

          const grossTariff = totalScheduleMw > 0 ? (totalGross / (totalScheduleMw * 250.0)) : 0;
          const netTariffPre = totalScheduleMw > 0 ? (totalNetPre / (totalScheduleMw * 250.0)) : 0;
          const netTariffPost = totalScheduleMw > 0 ? (totalNetPost / (totalScheduleMw * 250.0)) : 0;
          const actualTariff = totalGenerationMw > 0 ? (totalNetPost / (totalGenerationMw * 250.0)) : 0;

          const formatRupees = (val) => {
            const absVal = Math.abs(val);
            if (absVal >= 100000) return `₹ ${(val / 1000000).toFixed(2)} M`;
            return `₹ ${val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          };

          return {
            kpis: {
              gross_rev: formatRupees(totalGross),
              net_pre: formatRupees(totalNetPre),
              net_post: formatRupees(totalNetPost),
              total_charges: formatRupees(totalCharges),
            },
            tariffs: { gross: grossTariff, netPre: netTariffPre, netPost: netTariffPost, actual: actualTariff }
          };
        };

        const calcGenAndPlf = (resultsRes, numDays, defaultGen = '-', defaultPlf = '-') => {
          const rows = resultsRes?.data?.results;
          if (!rows || rows.length === 0) return { gen: defaultGen, plf: defaultPlf };
          let totalGenMw = 0;
          rows.forEach(r => {
            totalGenMw += Number(r.total_generation_mw) || 0;
          });
          if (totalGenMw === 0) return { gen: defaultGen, plf: defaultPlf };

          const genMWh = totalGenMw * 0.25;
          const genMU = genMWh / 1000.0;
          const maxMWh = 106.4 * numDays * 24.0;
          const plf = maxMWh > 0 ? (genMWh / maxMWh) * 100.0 : 0;

          return {
            gen: genMU.toFixed(2),
            plf: `${plf.toFixed(1)}%`
          };
        };

        const calcSalesMix = (resultsRes, defaultVol = '0.00', defaultBilat = 0, defaultExch = 0, defaultGdam = 0, defaultDam = 0, defaultRtm = 0) => {
          const rows = resultsRes?.data?.results;
          if (!rows || rows.length === 0) {
            return {
              totalMU: defaultVol,
              bilatPct: defaultBilat,
              exchPct: defaultExch,
              gdamPct: defaultGdam,
              damPct: defaultDam,
              rtmPct: defaultRtm,
              bilatVol: defaultVol * (defaultBilat / 100),
              exchVol: defaultVol * (defaultExch / 100),
              gdamVol: defaultVol * (defaultGdam / 100),
              damVol: defaultVol * (defaultDam / 100),
              rtmVol: defaultVol * (defaultRtm / 100),
            };
          }

          let contractVolKwh = 0;
          let gdamVolKwh = 0;
          let damVolKwh = 0;
          let rtmVolKwh = 0;

          rows.forEach(r => {
            contractVolKwh += Number(r.contract_schedule_vol) || 0;
            gdamVolKwh += Number(r.gdam_vol_exbus) || 0;
            damVolKwh += Number(r.dam_vol_exbus) || 0;
            rtmVolKwh += Number(r.rtm_vol_exbus) || 0;
          });

          const exchVolKwh = gdamVolKwh + damVolKwh + rtmVolKwh;
          const totalVolKwh = contractVolKwh + exchVolKwh;

          if (totalVolKwh === 0) {
            return {
              totalMU: defaultVol,
              bilatPct: defaultBilat,
              exchPct: defaultExch,
              gdamPct: defaultGdam,
              damPct: defaultDam,
              rtmPct: defaultRtm,
              bilatVol: defaultVol * (defaultBilat / 100),
              exchVol: defaultVol * (defaultExch / 100),
              gdamVol: defaultVol * (defaultGdam / 100),
              damVol: defaultVol * (defaultDam / 100),
              rtmVol: defaultVol * (defaultRtm / 100),
            };
          }

          const totalMU = (totalVolKwh / 1000000.0).toFixed(2);
          const bilatP = Math.round((contractVolKwh / totalVolKwh) * 100);
          const exchP = Math.round((exchVolKwh / totalVolKwh) * 100);
          const gdamP = Math.round((gdamVolKwh / totalVolKwh) * 100);
          const damP = Math.round((damVolKwh / totalVolKwh) * 100);
          const rtmP = Math.round((rtmVolKwh / totalVolKwh) * 100);

          return {
            totalMU: totalMU,
            bilatPct: bilatP,
            exchPct: exchP,
            gdamPct: gdamP,
            damPct: damP,
            rtmPct: rtmP,
            bilatVol: contractVolKwh,
            exchVol: exchVolKwh,
            gdamVol: gdamVolKwh,
            damVol: damVolKwh,
            rtmVol: rtmVolKwh,
          };
        };

        const daysMTD = Math.max(1, yesterday.date());
        const daysYTD = Math.max(1, yesterday.diff(today.startOf('year'), 'day') + 1);

        const yestMetrics = calcGenAndPlf(resYest, 1, '-', '-');
        const mtdMetrics = calcGenAndPlf(resMTD, daysMTD, '-', '-');
        const ytdMetrics = calcGenAndPlf(resYTD, daysYTD, '-', '-');
        // Calculate 3-year average annual generation and PLF
        const rows3YRaw = res3Y?.data?.results || [];
        let threeYearAvgGen = '-';
        let threeYearAvgPlf = '-';
        if (rows3YRaw.length > 0) {
          let total3YGenMw = 0;
          rows3YRaw.forEach(r => {
            total3YGenMw += Number(r.total_generation_mw) || 0;
          });
          if (total3YGenMw > 0) {
            const total3YGenMWh = total3YGenMw * 0.25;
            const avgAnnualGenMWh = total3YGenMWh / 3.0;
            const avgAnnualGenMU = avgAnnualGenMWh / 1000.0;
            const maxAnnualMWh = 106.4 * 365.25 * 24.0;
            const avg3YPlf = maxAnnualMWh > 0 ? (total3YGenMWh / maxAnnualMWh) * 100.0 : 0;
            threeYearAvgGen = avgAnnualGenMU.toFixed(2);
            threeYearAvgPlf = `${avg3YPlf.toFixed(1)}%`;
          }
        }

        const yestSales = calcSalesMix(resYest, '0.00', 0, 0, 0, 0, 0);
        const mtdSales = calcSalesMix(resMTD, '0.00', 0, 0, 0, 0, 0);
        const ytdSales = calcSalesMix(resYTD, '0.00', 0, 0, 0, 0, 0);

        setSalesMixData({
          yesterday: yestSales,
          mtd: mtdSales,
          ytd: ytdSales,
        });

        const calcRevenueMix = (resultsRes, mode = 'yesterday') => {
          const rows = resultsRes?.data?.results;
          if (!rows || rows.length === 0) return emptyRevMix;

          let bilatGross = 0;
          let exchGross = 0;
          let totalCharges = 0;
          let dsmPenalty = 0;
          let netRecPayable = 0;
          let totalNetPost = 0;
          let contractVolKwh = 0;
          let exchVolKwh = 0;
          let gdamVolKwh = 0;
          let totalSchedMw = 0;
          let totalGenMw = 0;

          const monthlyData = {};

          rows.forEach(r => {
            const rowBilatGross = Number(r.contract_gross_rev) || ((Number(r.gross_goa) || 0) + (Number(r.gross_nidar) || 0) + (Number(r.gross_npcl) || 0));
            const rowExchGross = Number(r.exchange_gross_rev) || 0;
            const rowCharges = Number(r.total_charges) || 0;
            const rowPenalty = r.total_penalty !== undefined && r.total_penalty !== null
              ? Number(r.total_penalty)
              : 0;

            bilatGross += rowBilatGross;
            exchGross += rowExchGross;
            totalCharges += rowCharges;
            dsmPenalty += rowPenalty;
            totalNetPost += Number(r.net_post) || 0;

            // Only rec - payable, no fallbacks
            const rowRecPayable = (Number(r.total_receivable) || 0) - (Number(r.total_payable) || 0);
            netRecPayable += rowRecPayable;

            const rowContractVol = Number(r.contract_schedule_vol) || 0;
            contractVolKwh += rowContractVol;
            const rowGdamExbus = Number(r.gdam_vol_exbus) || 0;
            const rowGdamCleared = r.gdam_cleared_vol_kwh !== undefined && r.gdam_cleared_vol_kwh !== null
              ? Number(r.gdam_cleared_vol_kwh)
              : rowGdamExbus;
            gdamVolKwh += rowGdamCleared;
            exchVolKwh += rowGdamExbus + (Number(r.dam_vol_exbus) || 0) + (Number(r.rtm_vol_exbus) || 0);
            totalSchedMw += Number(r.total_schedule_mw) || 0;
            const rowGenMw = Number(r.total_generation_mw) || 0;
            totalGenMw += rowGenMw;

            // Group by month for month-wise REC calculation
            const period = r.period || r.date || '';
            const monthKey = period.substring(0, 7) || 'all';
            if (!monthlyData[monthKey]) {
              monthlyData[monthKey] = { actualKwh: 0, bilatKwh: 0, gdamKwh: 0 };
            }
            monthlyData[monthKey].actualKwh += rowGenMw * 250.0;
            monthlyData[monthKey].bilatKwh += rowContractVol;
            monthlyData[monthKey].gdamKwh += rowGdamCleared;
          });

          const totalGross = bilatGross + exchGross + netRecPayable;
          const netRevenuePostDsm = totalNetPost > 0 ? totalNetPost : (totalGross - totalCharges - dsmPenalty);
          const totalVolKwh = (contractVolKwh + exchVolKwh) > 0 ? (contractVolKwh + exchVolKwh) : (totalSchedMw * 250.0);
          const actualGenKwh = totalGenMw * 250.0;

          const fmtM = (val) => {
            const absVal = Math.abs(val) / 1000000.0;
            const sign = val < 0 ? '-₹' : '₹';
            if (absVal >= 100) return `${sign}${Math.round(absVal)} M`;
            return `${sign}${absVal.toFixed(absVal >= 10 ? 1 : 2)} M`;
          };

          const fmtPU = (val) => {
            if (isNaN(val) || !isFinite(val)) return '0.00';
            return val.toFixed(2);
          };

          // Per unit calculations
          const bilatPerUnit = contractVolKwh > 0 ? bilatGross / contractVolKwh : 0;
          const exchPerUnit = exchVolKwh > 0 ? exchGross / exchVolKwh : 0;

          // Adjustment due to OI/UI per unit = ((exch_gross + bilat_gross + (Rec - Pay)) / actual_gen) - gross_PU
          const grossPU = totalVolKwh > 0 ? (bilatGross + exchGross) / totalVolKwh : 0;
          const netRecPayablePerUnit = actualGenKwh > 0
            ? (totalGross / actualGenKwh) - grossPU
            : 0;

          // REC calculation:
          // Formula: (actual - bilat - gdam) * 0.35, actual of all. Month-wise only.
          // For a month: total all individual values then calculate -> if < 0 discard else add in the YTD summation.
          // For MTD: simply sum individual values then calc.
          let recRev = 0;
          const isYesterday = mode === 'yesterday';
          if (isYesterday) {
            recRev = 0;
          } else if (mode === 'mtd') {
            const mtdSurplus = actualGenKwh - contractVolKwh - gdamVolKwh;
            recRev = mtdSurplus > 0 ? mtdSurplus * 0.35 : 0;
          } else if (mode === 'ytd') {
            Object.values(monthlyData).forEach(m => {
              const monthSurplus = m.actualKwh - m.bilatKwh - m.gdamKwh;
              if (monthSurplus > 0) {
                recRev += monthSurplus * 0.35;
              }
            });
          }
          const recPerUnit = actualGenKwh > 0 ? recRev / actualGenKwh : 0;

          // After Adjustment due to OI/UI row, all rows below are divided strictly by actual generation (actualGenKwh)
          const totalGrossPerUnit = actualGenKwh > 0 ? totalGross / actualGenKwh : 0;
          const chargesPerUnit = actualGenKwh > 0 ? totalCharges / actualGenKwh : 0;
          const dsmPenaltyPerUnit = actualGenKwh > 0 ? dsmPenalty / actualGenKwh : 0;
          const netRevenuePostDsmPerUnit = actualGenKwh > 0 ? netRevenuePostDsm / actualGenKwh : 0;

          const chargesMDisplay = totalCharges > 0
            ? `-₹${(totalCharges / 1000000.0).toFixed(totalCharges / 1000000.0 >= 100 ? 0 : (totalCharges / 1000000.0 >= 10 ? 1 : 2))} M`
            : '₹0.00 M';
          const dsmMDisplay = dsmPenalty > 0
            ? `-₹${(dsmPenalty / 1000000.0).toFixed(dsmPenalty / 1000000.0 >= 100 ? 0 : (dsmPenalty / 1000000.0 >= 10 ? 1 : 2))} M`
            : '₹0.00 M';
          const netRecPayableMDisplay = netRecPayable === 0 ? '₹0.00 M' : fmtM(netRecPayable);

          const chargesPUDisplay = chargesPerUnit > 0 ? `-${fmtPU(chargesPerUnit)}` : '0.00';
          const dsmPUDisplay = dsmPenaltyPerUnit > 0 ? `-${fmtPU(dsmPenaltyPerUnit)}` : '0.00';
          const netRecPayablePUDisplay = netRecPayablePerUnit === 0
            ? '0.00'
            : (netRecPayablePerUnit < 0 ? `-${fmtPU(Math.abs(netRecPayablePerUnit))}` : fmtPU(netRecPayablePerUnit));

          return {
            rev: {
              bilat: fmtM(bilatGross),
              exch: fmtM(exchGross),
              rec: isYesterday ? '-' : fmtM(recRev),
              recRaw: isYesterday ? null : recRev,
              totalGross: fmtM(totalGross),
              netRecPayable: netRecPayableMDisplay,
              netRecPayableRaw: netRecPayable,
              charges: chargesMDisplay,
              chargesRaw: -Math.abs(totalCharges),
              dsm: dsmMDisplay,
              dsmRaw: -Math.abs(dsmPenalty),
              netPostDsm: fmtM(netRevenuePostDsm),
            },
            pu: {
              bilat: fmtPU(bilatPerUnit),
              exch: fmtPU(exchPerUnit),
              rec: isYesterday ? '-' : fmtPU(recPerUnit),
              recRaw: isYesterday ? null : recPerUnit,
              totalGross: fmtPU(totalGrossPerUnit),
              netRecPayable: netRecPayablePUDisplay,
              netRecPayableRaw: netRecPayablePerUnit,
              charges: chargesPUDisplay,
              chargesRaw: -Math.abs(chargesPerUnit),
              dsm: dsmPUDisplay,
              dsmRaw: -Math.abs(dsmPenaltyPerUnit),
              netPostDsm: fmtPU(netRevenuePostDsmPerUnit),
            }
          };
        };

        const yestRevMix = calcRevenueMix(resYest, 'yesterday');
        const mtdRevMix = calcRevenueMix(resMTD, 'mtd');
        const ytdRevMix = calcRevenueMix(resYTD, 'ytd');

        setRevenueMixData({
          yesterday: yestRevMix,
          mtd: mtdRevMix,
          ytd: ytdRevMix,
        });

        // Calculate YTD Waterfall per unit
        const rowsYTD = resYTD?.data?.results || [];
        if (rowsYTD.length > 0) {
          let totalGross = 0, totalNetPre = 0, totalNetPost = 0, totalCharges = 0;
          let contractVolKwh = 0, exchVolKwh = 0;
          let sumStu = 0, sumNldc = 0, sumTrader = 0, sumExch = 0, sumTax = 0, sumRebate = 0, sumPenalty = 0;
          let sumReceivable = 0, sumPayable = 0;
          let totalSchedMw = 0;

          let totalGenMw = 0;
          rowsYTD.forEach(r => {
            const rowBilatGross = Number(r.contract_gross_rev) || ((Number(r.gross_goa) || 0) + (Number(r.gross_nidar) || 0) + (Number(r.gross_npcl) || 0));
            const rowExchGross = Number(r.exchange_gross_rev) || 0;
            const rowGross = (rowBilatGross + rowExchGross) || (Number(r.gross_rev) || 0);

            totalGross += rowGross;
            totalNetPre += Number(r.net_pre) || 0;
            totalNetPost += Number(r.net_post) || 0;
            totalCharges += Number(r.total_charges) || 0;
            contractVolKwh += Number(r.contract_schedule_vol) || 0;
            exchVolKwh += (Number(r.gdam_vol_exbus) || 0) + (Number(r.dam_vol_exbus) || 0) + (Number(r.rtm_vol_exbus) || 0);
            totalSchedMw += Number(r.total_schedule_mw) || 0;
            totalGenMw += Number(r.total_generation_mw) || 0;

            const rowPenalty = r.total_penalty !== undefined && r.total_penalty !== null
              ? Number(r.total_penalty)
              : 0;
            sumPenalty += rowPenalty;

            sumReceivable += Number(r.total_receivable) || 0;
            sumPayable += Number(r.total_payable) || 0;

            sumStu += (Number(r.stu_charges_exch) || 0) + (Number(r.stu_charges_goa) || 0) + (Number(r.stu_charges_nidar) || 0) + (Number(r.stu_charges_npcl) || 0);
            sumNldc += (Number(r.nldc_fees_exch) || 0) + (Number(r.nldc_fees_goa) || 0) + (Number(r.nldc_fees_nidar) || 0) + (Number(r.nldc_fees_npcl) || 0);
            sumTrader += (Number(r.trader_comm_exch) || 0) + (Number(r.trader_comm_goa) || 0) + (Number(r.trader_comm_nidar) || 0) + (Number(r.trader_comm_npcl) || 0);
            sumExch += (Number(r.exch_comm_exch) || 0) + (Number(r.exch_comm_goa) || 0) + (Number(r.exch_comm_nidar) || 0) + (Number(r.exch_comm_npcl) || 0);
            sumTax += (Number(r.tax_exch) || 0) + (Number(r.tax_goa) || 0) + (Number(r.tax_nidar) || 0) + (Number(r.tax_npcl) || 0);
            sumRebate += (Number(r.rebate_exch) || 0) + (Number(r.rebate_goa) || 0) + (Number(r.rebate_nidar) || 0) + (Number(r.rebate_npcl) || 0);
          });

          const totalVolKwh = (contractVolKwh + exchVolKwh) > 0 ? (contractVolKwh + exchVolKwh) : (totalSchedMw * 250.0);
          const actualGenKwh = totalGenMw * 250.0;

          if (totalVolKwh > 0 && (totalNetPre > 0 || totalNetPost > 0 || totalGross > 0)) {
            const grossPU = totalGross / totalVolKwh;
            const netRecPayable = sumReceivable - sumPayable;
            const recPayPU = netRecPayable / totalVolKwh;
            const chargesPU = actualGenKwh > 0 ? totalCharges / actualGenKwh : 0;
            const penaltyPU = actualGenKwh > 0 ? sumPenalty / actualGenKwh : 0;
            const netRevenuePostDsm = totalNetPost > 0 ? totalNetPost : (totalGross - totalCharges + netRecPayable - sumPenalty);
            const netPostPU = actualGenKwh > 0 ? netRevenuePostDsm / actualGenKwh : 0;

            let currentLevel = grossPU;

            // Step 1: Net Receivable/Payable
            let recPayBottom, recPayTop;
            const isReceivable = recPayPU >= 0;
            if (isReceivable) {
              recPayBottom = currentLevel;
              recPayTop = currentLevel + recPayPU;
              currentLevel = recPayTop;
            } else {
              recPayTop = currentLevel;
              recPayBottom = Math.max(0, currentLevel - Math.abs(recPayPU));
              currentLevel = recPayBottom;
            }

            // Step 2: Charges
            const chargesTop = currentLevel;
            const chargesBottom = Math.max(0, currentLevel - chargesPU);
            currentLevel = chargesBottom;

            // Step 3: DSM Penalty
            const dsmTop = currentLevel;
            const dsmBottom = Math.max(0, currentLevel - penaltyPU);
            currentLevel = dsmBottom;

            setYtdWaterfallData([
              { name: 'Gross revenue', displayValue: [0, grossPU], color: '#0052CC', raw: grossPU },
              {
                name: 'Net Receivable/Payable',
                displayValue: [recPayBottom, recPayTop],
                color: isReceivable ? '#16a34a' : '#ef4444',
                raw: recPayPU
              },
              { name: 'Charges', displayValue: [chargesBottom, chargesTop], color: '#E74C3C', raw: -chargesPU },
              { name: 'DSM Penalty', displayValue: [dsmBottom, dsmTop], color: '#DC2626', raw: -penaltyPU },
              { name: 'Revenue post-DSM', displayValue: [0, netPostPU], color: '#E67E22', raw: netPostPU },
            ]);

            setYtdChargesData([
              { name: 'STU Charges', value: actualGenKwh > 0 ? sumStu / actualGenKwh : 0, key: 'stu_charges' },
              { name: 'Scheduling Charges', value: actualGenKwh > 0 ? sumNldc / actualGenKwh : 0, key: 'nldc_fees' },
              { name: "Trader's Comm.", value: actualGenKwh > 0 ? sumTrader / actualGenKwh : 0, key: 'trader_comm' },
              { name: 'Exchange Comm.', value: actualGenKwh > 0 ? sumExch / actualGenKwh : 0, key: 'exch_comm' },
              { name: 'Tax / Rebate', value: actualGenKwh > 0 ? (sumTax + sumRebate) / actualGenKwh : 0, key: 'tax' },
              { name: 'DSM Penalty', value: penaltyPU, key: 'dsm_penalty' },
            ]);
          }
        }

        setGenPlfData({
          yesterdayLabel: yesterdayDateLabel,
          yesterdayGen: yestMetrics.gen,
          yesterdayPlf: yestMetrics.plf,
          mtdGen: mtdMetrics.gen,
          mtdPlf: mtdMetrics.plf,
          ytdGen: ytdMetrics.gen,
          ytdPlf: ytdMetrics.plf,
          threeYearAvgGen,
          threeYearAvgPlf,
        });

        setSummaryData({
          mtd: processResults(resMTD),
          ytd: processResults(resYTD),
          std: processResults(resSTD),
        });

        // Build monthly array for 5th Block Chart (a): Month-wise net revenue — plant starting from April 2025 up to current month
        const rows3Y = res3Y?.data?.results || [];
        const monthsNetRev = [];
        const months1YVol = [];

        const startNetRevDate = dayjs('2025-04-01');
        // Dynamically include only months up to the current month
        const totalNetRevMonths = Math.max(0, today.diff(startNetRevDate, 'month') + 1);

        for (let i = 0; i < totalNetRevMonths; i++) {
          const currentDate = startNetRevDate.add(i, 'month');
          if (currentDate.isAfter(today, 'month')) break;
          const monthStr = currentDate.format('YYYY-MM');
          const monthLabel = currentDate.format('MMM-YY');

          const dbRow = rows3Y.find(r => r.period && r.period.startsWith(monthStr));

          let netRevValM = 0;
          let plantPUVal = 0;
          let exchPUVal = 0;
          let bilatVolMU = 0;
          let exchVolMU = 0;

          if (dbRow) {
            const netPost = Number(dbRow.net_post) || Number(dbRow.net_pre) || 0;
            const contractVolKwh = Number(dbRow.contract_schedule_vol) || 0;
            const exchVolKwh = (Number(dbRow.gdam_vol_exbus) || 0) + (Number(dbRow.dam_vol_exbus) || 0) + (Number(dbRow.rtm_vol_exbus) || 0);
            const totalVolKwh = contractVolKwh + exchVolKwh;
            const exchNetRev = Number(dbRow.net_exch) || Number(dbRow.exchange_gross_rev) || 0;

            const exchClearedVolKwh = (Number(dbRow.gdam_cleared_vol_kwh) || 0) + (Number(dbRow.dam_cleared_vol_kwh) || 0) + (Number(dbRow.rtm_cleared_vol_kwh) || 0);
            const effExchClearedVol = exchClearedVolKwh > 0 ? exchClearedVolKwh : exchVolKwh;
            if (netPost > 0) netRevValM = netPost / 1000000.0;
            if (totalVolKwh > 0 && netPost > 0) plantPUVal = netPost / totalVolKwh;
            if (effExchClearedVol > 0 && exchNetRev > 0) exchPUVal = exchNetRev / effExchClearedVol;
            if (contractVolKwh > 0) bilatVolMU = contractVolKwh / 1000000.0;
            if (exchVolKwh > 0) exchVolMU = exchVolKwh / 1000000.0;
          }

          const combVolMU = Number((bilatVolMU + exchVolMU).toFixed(2));
          const exchSharePct = combVolMU > 0 ? Number(((exchVolMU / combVolMU) * 100).toFixed(1)) : 0;
          const bilatSharePct = combVolMU > 0 ? Number(((bilatVolMU / combVolMU) * 100).toFixed(1)) : 0;

          const rec = {
            period: monthStr,
            monthLabel: monthLabel,
            netRevenueM: Number(netRevValM.toFixed(2)),
            rawRevenue: netRevValM * 1000000,
            plantPU: Number(plantPUVal.toFixed(2)),
            exchPU: Number(exchPUVal.toFixed(2)),
            bilatVolMU: Number(bilatVolMU.toFixed(2)),
            exchVolMU: Number(exchVolMU.toFixed(2)),
            totalVolMU: combVolMU,
            exchSharePct: exchSharePct,
            bilatSharePct: bilatSharePct
          };

          monthsNetRev.push(rec);

          // Bilateral vs. Exchange Volume: start from April 2025 (matching monthsNetRev sequence)
          months1YVol.push(rec);
        }
        setChart5Data(monthsNetRev);
        setChart5dData(months1YVol);

        // Build 12-month array (Jan to Dec) with 2024, 2025, 2026 lines for Plant PU and Exchange PU
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const plantPu12M = [];
        const exchPu12M = [];

        for (let m = 1; m <= 12; m++) {
          const monthNumStr = String(m).padStart(2, '0');
          const monthLabel = monthNames[m - 1];

          const row2024 = rows3Y.find(r => r.period && r.period.startsWith(`2024-${monthNumStr}`));
          const row2025 = rows3Y.find(r => r.period && r.period.startsWith(`2025-${monthNumStr}`));
          const row2026 = rows3Y.find(r => r.period && r.period.startsWith(`2026-${monthNumStr}`));

          const getPlantPU = (r) => {
            if (!r) return null;
            // Divide Net Revenue Post DSM by actual generation (total_generation_mw * 250 kWh)
            const netPost = Number(r.net_post) || 0;
            const actualGenMw = Number(r.total_generation_mw) || Number(r.actual_generation_mw) || 0;
            const actualGenKwh = actualGenMw * 250.0;
            if (actualGenKwh > 0 && netPost > 0) return Number((netPost / actualGenKwh).toFixed(2));
            return null;
          };

          const getExchPU = (r) => {
            if (!r) return null;
            const exchNetRev = Number(r.net_exch) || 0;
            const clearedVolKwh = (Number(r.gdam_cleared_vol_kwh) || 0) + (Number(r.dam_cleared_vol_kwh) || 0) + (Number(r.rtm_cleared_vol_kwh) || 0);
            const exchVolKwh = clearedVolKwh > 0 ? clearedVolKwh : ((Number(r.gdam_vol_exbus) || 0) + (Number(r.dam_vol_exbus) || 0) + (Number(r.rtm_vol_exbus) || 0));
            if (exchVolKwh > 0 && exchNetRev > 0) return Number((exchNetRev / exchVolKwh).toFixed(2));
            return null;
          };

          // Static fallback data for Exchange PU where API data is missing
          const exchPuFallback = {
            '2024-08': 2.64,
            '2024-09': 3.45,
            '2024-10': 3.35,
            '2024-11': 3.02,
            '2024-12': 3.43,
            '2025-01': 4.70,
            '2025-02': 4.24,
            '2025-03': 4.24,
          };

          plantPu12M.push({
            month: monthLabel,
            monthNum: m,
            '2024': getPlantPU(row2024),
            '2025': getPlantPU(row2025),
            '2026': getPlantPU(row2026),
          });

          exchPu12M.push({
            month: monthLabel,
            monthNum: m,
            '2024': getExchPU(row2024) ?? exchPuFallback[`2024-${monthNumStr}`] ?? null,
            '2025': getExchPU(row2025) ?? exchPuFallback[`2025-${monthNumStr}`] ?? null,
            '2026': getExchPU(row2026),
          });
        }

        setChartPlantPu3YData(plantPu12M);
        setChartExchPu3YData(exchPu12M);

        // Process 30-day DAM, GDAM, and RTM volume data
        const rows30D = res30D?.data?.results || [];
        const dailyExchVol = [];
        for (let d = 30; d >= 1; d--) {
          const currDay = today.subtract(d, 'day');
          const dayStr = currDay.format('YYYY-MM-DD');
          const dayLabel = currDay.format('DD-MMM');

          const r = rows30D.find(row => row.period && row.period.startsWith(dayStr));

          const gdamKwh = r
            ? (r.gdam_cleared_vol_kwh !== undefined && r.gdam_cleared_vol_kwh !== null
              ? Number(r.gdam_cleared_vol_kwh)
              : (Number(r.gdam_vol_exbus) || 0))
            : 0;
          const damKwh = r
            ? (r.dam_cleared_vol_kwh !== undefined && r.dam_cleared_vol_kwh !== null
              ? Number(r.dam_cleared_vol_kwh)
              : (Number(r.dam_vol_exbus) || 0))
            : 0;
          const rtmKwh = r
            ? (r.rtm_cleared_vol_kwh !== undefined && r.rtm_cleared_vol_kwh !== null
              ? Number(r.rtm_cleared_vol_kwh)
              : (Number(r.rtm_vol_exbus) || 0))
            : 0;

          const gdamMU = Number((gdamKwh / 1000000.0).toFixed(3));
          const damMU = Number((damKwh / 1000000.0).toFixed(3));
          const rtmMU = Number((rtmKwh / 1000000.0).toFixed(3));
          const totalExchMU = Number((gdamMU + damMU + rtmMU).toFixed(3));

          dailyExchVol.push({
            date: dayStr,
            dayLabel,
            gdamMU,
            damMU,
            rtmMU,
            totalExchMU,
          });
        }
        setChart30dExchVolData(dailyExchVol);

        // Process Yesterday Time-Block (15-min) Cleared Volume on Exchange & Market Price (Weighted Average)
        const rowsYestMarket = resYestMarket15M?.data?.results || [];
        const blockMap = new Map();
        rowsYestMarket.forEach(row => {
          if (!row.period) return;
          const cleanPeriod = row.period.split(' - ')[0].trim();
          const timeVal = cleanPeriod.includes(' ') ? cleanPeriod.split(' ')[1] : cleanPeriod;
          blockMap.set(timeVal, row);
          blockMap.set(cleanPeriod, row);
        });

        const yestBlocks = [];
        for (let b = 1; b <= 96; b++) {
          const totalMins = (b - 1) * 15;
          const h = String(Math.floor(totalMins / 60)).padStart(2, '0');
          const m = String(totalMins % 60).padStart(2, '0');
          const timeStr = `${h}:${m}`;
          const periodFull = `${yesterdayStr} ${timeStr}`;

          const existing = blockMap.get(timeStr) || blockMap.get(periodFull);

          // Cleared volume of segments in MW (if in kWh exbus, divide by 250; or from plant_vol_mu / total_vol_mu)
          // Look for direct segment volumes:
          let gdamMw = 0;
          let damMw = 0;
          let rtmMw = 0;

          if (existing) {
            if (existing.gdam_cleared_volume_mw != null) {
              gdamMw = Number(existing.gdam_cleared_volume_mw) || 0;
            } else if (existing.gdam_vol_exbus != null) {
              gdamMw = (Number(existing.gdam_vol_exbus) || 0) / 250.0;
            } else if (existing.gdam_cleared_volume_kwh != null) {
              gdamMw = (Number(existing.gdam_cleared_volume_kwh) || 0) / 250.0;
            } else if (existing.plant_gdam_vol_mu != null) {
              gdamMw = (Number(existing.plant_gdam_vol_mu) * 1000000.0) / 250.0;
            }

            if (existing.dam_cleared_volume_mw != null) {
              damMw = Number(existing.dam_cleared_volume_mw) || 0;
            } else if (existing.dam_vol_exbus != null) {
              damMw = (Number(existing.dam_vol_exbus) || 0) / 250.0;
            } else if (existing.dam_cleared_volume_kwh != null) {
              damMw = (Number(existing.dam_cleared_volume_kwh) || 0) / 250.0;
            } else if (existing.plant_dam_vol_mu != null) {
              damMw = (Number(existing.plant_dam_vol_mu) * 1000000.0) / 250.0;
            }

            if (existing.rtm_cleared_volume_mw != null) {
              rtmMw = Number(existing.rtm_cleared_volume_mw) || 0;
            } else if (existing.rtm_vol_exbus != null) {
              rtmMw = (Number(existing.rtm_vol_exbus) || 0) / 250.0;
            } else if (existing.rtm_cleared_volume_kwh != null) {
              rtmMw = (Number(existing.rtm_cleared_volume_kwh) || 0) / 250.0;
            } else if (existing.plant_rtm_vol_mu != null) {
              rtmMw = (Number(existing.plant_rtm_vol_mu) * 1000000.0) / 250.0;
            }
          }

          // Weighted average market price (₹/kWh)
          let wtdMcpVal = null;
          if (existing) {
            if (existing.wtd_mcp != null && Number(existing.wtd_mcp) > 0) {
              wtdMcpVal = Number(existing.wtd_mcp);
            } else if (existing.wtd_mcp_rate != null && Number(existing.wtd_mcp_rate) > 0) {
              wtdMcpVal = Number(existing.wtd_mcp_rate);
            } else {
              // Calculate volume-weighted or segment MCP if available
              const gMcp = Number(existing.gdam_mcp) || 0;
              const dMcp = Number(existing.dam_mcp) || 0;
              const rMcp = Number(existing.rtm_mcp) || 0;
              const totVol = gdamMw + damMw + rtmMw;
              if (totVol > 0) {
                wtdMcpVal = (gdamMw * gMcp + damMw * dMcp + rtmMw * rMcp) / totVol;
              } else if (gMcp > 0 || dMcp > 0 || rMcp > 0) {
                const count = (gMcp > 0 ? 1 : 0) + (dMcp > 0 ? 1 : 0) + (rMcp > 0 ? 1 : 0);
                wtdMcpVal = (gMcp + dMcp + rMcp) / count;
              }
            }
          }

          const totVolMw = Number((gdamMw + damMw + rtmMw).toFixed(2));

          const endMins = b * 15;
          const endH = String(Math.floor(endMins / 60)).padStart(2, '0');
          const endM = String(endMins % 60).padStart(2, '0');
          const timeInterval = `${timeStr} - ${endH}:${endM}`;

          let ourRealizedVal = 0;
          if (existing && totVolMw > 0) {
            if (existing.gross_tariff_realized_exch != null && Number(existing.gross_tariff_realized_exch) > 0) {
              ourRealizedVal = Number(existing.gross_tariff_realized_exch);
            } else if (existing.realized_exch != null && Number(existing.realized_exch) > 0) {
              ourRealizedVal = Number(existing.realized_exch);
            } else if (existing.exchange_gross_revenue != null && Number(existing.exchange_gross_revenue) > 0) {
              ourRealizedVal = Number(existing.exchange_gross_revenue) / (totVolMw * 250.0);
            }
          }

          yestBlocks.push({
            block: b,
            time: timeStr,
            timeInterval: timeInterval,
            period: timeStr,
            gdam_cleared: Number(gdamMw.toFixed(2)),
            dam_cleared: Number(damMw.toFixed(2)),
            rtm_cleared: Number(rtmMw.toFixed(2)),
            total_cleared: totVolMw,
            wtd_mcp: wtdMcpVal != null ? Number(wtdMcpVal.toFixed(2)) : null,
            our_realized: Number(ourRealizedVal.toFixed(2)),
          });
        }
        setChartYestExchBlockVolData(yestBlocks);

        // Process Month-wise REC (YTD) from resYTD (monthly granularity)
        const ytdRecRows = resYTD?.data?.results || [];
        const ytdRecMonthly = [];
        const currentYear = today.year();
        const currentMonth = today.month(); // 0-indexed

        // Group rows by month (YYYY-MM)
        const monthDataMap = {};
        ytdRecRows.forEach(r => {
          const period = r.period || r.date || '';
          const monthKey = period.substring(0, 7); // YYYY-MM
          if (!monthKey) return;
          if (!monthDataMap[monthKey]) {
            monthDataMap[monthKey] = { actualKwh: 0, bilatKwh: 0, gdamKwh: 0 };
          }
          const rowGdamCleared = r.gdam_cleared_vol_kwh !== undefined && r.gdam_cleared_vol_kwh !== null
            ? Number(r.gdam_cleared_vol_kwh)
            : (Number(r.gdam_vol_exbus) || 0);
          monthDataMap[monthKey].actualKwh += (Number(r.total_generation_mw) || 0) * 250.0;
          monthDataMap[monthKey].bilatKwh += Number(r.contract_schedule_vol) || 0;
          monthDataMap[monthKey].gdamKwh += rowGdamCleared;
        });

        for (let m = 0; m <= currentMonth; m++) {
          const monthDate = today.year(currentYear).month(m).startOf('month');
          const monthStr = monthDate.format('YYYY-MM');
          const monthLabel = monthDate.format('MMM-YY');

          const mData = monthDataMap[monthStr] || { actualKwh: 0, bilatKwh: 0, gdamKwh: 0 };
          const actualGenKwh = mData.actualKwh;
          const monthSurplus = actualGenKwh - mData.bilatKwh - mData.gdamKwh;
          const recRev = monthSurplus > 0 ? monthSurplus * 0.35 : 0;
          const recVolKwh = monthSurplus > 0 ? monthSurplus : 0;

          const recRevM = Number((recRev / 1000000.0).toFixed(2));
          const recPU = actualGenKwh > 0 ? Number((recRev / actualGenKwh).toFixed(2)) : 0;
          const recVolMU = Number((recVolKwh / 1000000.0).toFixed(2));

          ytdRecMonthly.push({
            period: monthStr,
            monthLabel,
            recRevM,
            recRevRaw: recRev,
            recPU,
            recVolMU,
            actualGenMU: Number((actualGenKwh / 1000000.0).toFixed(2)),
          });
        }
        setChartYtdRecData(ytdRecMonthly);
      } catch (err) {
        console.error("Error fetching summary data", err);
      }
      setLoading(false);
    };
    fetchSummary();
  }, [apiParamsString]);

  const renderSection = (title, data, accentColor) => (
    <Card
      bordered={true}
      bodyStyle={{ padding: 0 }}
      style={{
        marginBottom: 16,
        borderRadius: 8,
        overflow: 'hidden',
        border: '1px solid #e8e8e8',
        borderTop: `4px solid ${accentColor}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        transition: 'all 0.3s ease',
        background: '#fff'
      }}
    >
      <div style={{
        padding: '20px 24px 16px',
        borderBottom: '1px solid #f0f0f0',
        position: 'relative'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', color: '#555' }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#333' }}>{title}</div>
        </div>

        {data ? (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 2, fontWeight: 500 }}>Net Revenue Post-DSM</div>
            <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1, color: '#1B4F72' }}>{data.kpis.net_post}</div>
            <div style={{ fontSize: 11, fontWeight: 500, marginTop: 8, background: '#f5f5f5', color: '#555', padding: '4px 8px', borderRadius: 4, display: 'inline-block' }}>
              Net Tariff Post DSM: <strong style={{ color: '#1B4F72' }}>{data.tariffs.netPost.toFixed(2)} ₹/kWh</strong>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', height: 86 }}>
            <Spin size="small" />
          </div>
        )}
      </div>

      <div style={{ padding: '16px 24px 20px', background: '#fff' }}>
        {data ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f5f5f5', paddingBottom: 10 }}>
              <div>
                <div style={{ fontSize: 12, color: '#555', fontWeight: 500 }}>Gross Revenue</div>
                <div style={{ fontSize: 10, color: '#999', marginTop: 1 }}>Gross Tariff: {data.tariffs.gross.toFixed(2)} ₹/kWh</div>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1B4F72' }}>{data.kpis.gross_rev}</div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f5f5f5', paddingBottom: 10 }}>
              <div>
                <div style={{ fontSize: 12, color: '#555', fontWeight: 500 }}>Revenue Pre-DSM</div>
                <div style={{ fontSize: 10, color: '#999', marginTop: 1 }}>Net Tariff Pre DSM: {data.tariffs.netPre.toFixed(2)} ₹/kWh</div>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1B4F72' }}>{data.kpis.net_pre}</div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 12, color: '#555', fontWeight: 500 }}>Charges & Commission</div>
                <div style={{ fontSize: 10, color: '#999', marginTop: 1 }}>Actual Tariff: {data.tariffs.actual.toFixed(2)} ₹/kWh</div>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1B4F72' }}>{data.kpis.total_charges}</div>
            </div>
          </div>
        ) : (
          <div style={{ height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin size="small" />
          </div>
        )}
      </div>
    </Card>
  );

  return (
    <Spin spinning={loading}>
      <div style={{ paddingTop: 16 }}>
        {/* 1st Block: Plant Information & Generation/PLF */}
        <Card
          bordered={true}
          headStyle={{
            background: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            padding: '12px 20px',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8
          }}
          style={{
            marginBottom: 16,
            borderRadius: 8,
            background: '#fff',
            border: '1px solid #cbd5e1',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
          }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1B4F72', lineHeight: '1.2' }}>Andra Lake Power Plant</div>
                <div style={{ fontSize: 12, fontWeight: 500, color: '#64748b', marginTop: 2 }}>Plant Information & Performance Overview</div>
              </div>
              {/* Sleek inline plant info chips right next to the title */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: 6,
                padding: '4px 14px',
                gap: 16,
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
              }}>
                <div>
                  <span style={{ fontSize: 11, fontWeight: 500, color: '#64748b', marginRight: 6 }}>Capacity:</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>106.4 MW</span>
                </div>
                <div style={{ width: 1, height: 14, background: '#cbd5e1' }} />
                <div>
                  <span style={{ fontSize: 11, fontWeight: 500, color: '#64748b', marginRight: 6 }}>Commissioned:</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>2012</span>
                </div>
                <div style={{ width: 1, height: 14, background: '#cbd5e1' }} />
                <div>
                  <span style={{ fontSize: 11, fontWeight: 500, color: '#64748b', marginRight: 6 }}>Voltage:</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>132 kV</span>
                </div>
                <div style={{ width: 1, height: 14, background: '#cbd5e1' }} />
                <div>
                  <span style={{ fontSize: 11, fontWeight: 500, color: '#64748b', marginRight: 6 }}>Substation:</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Andra Lake</span>
                </div>
              </div>
            </div>
          }
          bodyStyle={{ padding: '16px 20px 18px' }}
        >
          {/* Generation & Plant Load Factor (PLF) Section */}
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#1B4F72', marginBottom: 10 }}>
              Generation & Plant Load Factor (PLF)
            </div>

            <div style={{
              border: '1px solid #cbd5e1',
              borderRadius: 8,
              overflow: 'hidden',
              background: '#fff'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: 'Inter, sans-serif' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #cbd5e1' }}>
                    <th style={{ textAlign: 'left', padding: '10px 16px', color: '#1e293b', fontWeight: 700, fontSize: 12.5, width: '32%' }}>Metric</th>
                    <th style={{ textAlign: 'right', padding: '10px 16px', color: '#1e293b', fontWeight: 700, fontSize: 12.5, width: '17%' }}>
                      {genPlfData.yesterdayLabel}
                    </th>
                    <th style={{ textAlign: 'right', padding: '10px 16px', color: '#1e293b', fontWeight: 700, fontSize: 12.5, width: '17%' }}>
                      MTD
                    </th>
                    <th style={{ textAlign: 'right', padding: '10px 16px', color: '#1e293b', fontWeight: 700, fontSize: 12.5, width: '17%' }}>
                      YTD
                    </th>
                    <th style={{ textAlign: 'right', padding: '10px 16px', color: '#1e293b', fontWeight: 700, fontSize: 12.5, width: '17%' }}>
                      3Y Avg
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: '#334155' }}>
                      Generation (MU)
                    </td>
                    <td style={{ textAlign: 'right', padding: '12px 16px', fontWeight: 700, fontSize: 13, color: '#0f172a' }}>
                      {genPlfData.yesterdayGen}
                    </td>
                    <td style={{ textAlign: 'right', padding: '12px 16px', fontWeight: 700, fontSize: 13, color: '#0f172a' }}>
                      {genPlfData.mtdGen}
                    </td>
                    <td style={{ textAlign: 'right', padding: '12px 16px', fontWeight: 700, fontSize: 13, color: '#0f172a' }}>
                      {genPlfData.ytdGen}
                    </td>
                    <td style={{ textAlign: 'right', padding: '12px 16px', fontWeight: 700, fontSize: 13, color: '#0f172a' }}>
                      214.15
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: '#334155' }}>
                      Plant Load Factor (PLF)
                    </td>
                    <td style={{ textAlign: 'right', padding: '12px 16px', fontWeight: 700, fontSize: 13, color: '#0f172a' }}>
                      {genPlfData.yesterdayPlf}
                    </td>
                    <td style={{ textAlign: 'right', padding: '12px 16px', fontWeight: 700, fontSize: 13, color: '#0f172a' }}>
                      {genPlfData.mtdPlf}
                    </td>
                    <td style={{ textAlign: 'right', padding: '12px 16px', fontWeight: 700, fontSize: 13, color: '#0f172a' }}>
                      {genPlfData.ytdPlf}
                    </td>
                    <td style={{ textAlign: 'right', padding: '12px 16px', fontWeight: 700, fontSize: 13, color: '#0f172a' }}>
                      23.37 %
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{ textAlign: 'right', marginTop: 8, fontSize: 11.5, fontWeight: 500, color: '#64748b' }}>
              (Actual Generation)
            </div>
          </div>
        </Card>

        {/* 2nd Block: Sales Mix */}
        <Card
          bordered={true}
          headStyle={SUMMARY_CARD_HEAD_STYLE}
          title={<span style={{ fontSize: 15, fontWeight: 700, color: '#1B4F72' }}>Sales mix</span>}
          extra={
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', fontSize: 12, color: '#475569', fontWeight: 500 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: '#5B9BD5' }} /> Bilateral
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: '#418AB3' }} /> Exchange
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: '#48C9B0' }} /> GDAM
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: '#B2B7F2' }} /> DAM
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: '#F8A51D' }} /> RTM
              </span>
            </div>
          }
          style={{
            marginBottom: 20,
            borderRadius: 8,
            background: '#fff',
            border: SUMMARY_BOX_BORDER,
            overflow: 'hidden'
          }}
          bodyStyle={{ padding: '20px 24px' }}
        >

          <Row gutter={[24, 20]} justify="space-around">
            <Col xs={24} sm={8}>
              <SalesMixPieItem title={genPlfData.yesterdayLabel} data={salesMixData.yesterday} />
            </Col>
            <Col xs={24} sm={8}>
              <SalesMixPieItem title="MTD" data={salesMixData.mtd} />
            </Col>
            <Col xs={24} sm={8}>
              <SalesMixPieItem title="YTD" data={salesMixData.ytd} />
            </Col>
          </Row>
          <div style={{ textAlign: 'right', marginTop: 8, fontSize: 11.5, fontWeight: 500, color: '#64748b' }}>
            (Units Sold)
          </div>
        </Card>

        {/* 3rd Block: Revenue Mix */}
        <Card
          bordered={true}
          headStyle={SUMMARY_CARD_HEAD_STYLE}
          title={<span style={{ fontSize: 15, fontWeight: 700, color: '#1B4F72' }}>Revenue mix</span>}
          style={{
            marginBottom: 16,
            borderRadius: 8,
            background: '#fff',
            border: SUMMARY_BOX_BORDER,
            overflow: 'hidden'
          }}
          bodyStyle={{ padding: '16px 24px 20px' }}
        >
          <Row gutter={[32, 20]}>
            {/* Actual Values Block: Revenue (₹M) */}
            <Col xs={24} lg={12}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1B4F72', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>
                Revenue (₹ M)
              </div>
              <div style={{
                border: SUMMARY_BOX_BORDER,
                borderRadius: 6,
                padding: '8px 14px',
                background: '#fff',
                overflowX: 'auto'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, fontFamily: 'Inter, sans-serif' }}>
                  <thead>
                    <tr style={{ borderBottom: '1.5px solid #cbd5e1' }}>
                      <th style={{ textAlign: 'left', paddingBottom: 6, color: '#64748b', fontWeight: 600, fontSize: 11.5, width: '42%' }}></th>
                      <th style={{ textAlign: 'right', paddingBottom: 6, paddingRight: 12, color: '#64748b', fontWeight: 600, fontSize: 11.5, width: '20%' }}>{genPlfData.yesterdayLabel}</th>
                      <th style={{ textAlign: 'right', paddingBottom: 6, paddingRight: 12, color: '#64748b', fontWeight: 600, fontSize: 11.5, width: '19%' }}>MTD</th>
                      <th style={{ textAlign: 'right', paddingBottom: 6, color: '#64748b', fontWeight: 600, fontSize: 11.5, width: '19%' }}>YTD</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '6px 0', color: '#1e293b', fontWeight: 500 }}>Bilateral</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: '#0f172a' }}>{revenueMixData.yesterday.rev.bilat}</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: '#0f172a' }}>{revenueMixData.mtd.rev.bilat}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>{revenueMixData.ytd.rev.bilat}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '6px 0', color: '#1e293b', fontWeight: 500 }}>Exchange</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: '#0f172a' }}>{revenueMixData.yesterday.rev.exch}</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: '#0f172a' }}>{revenueMixData.mtd.rev.exch}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>{revenueMixData.ytd.rev.exch}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '6px 0', color: '#1e293b', fontWeight: 500 }}>Adjustment due to OI/UI</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: revenueMixData.yesterday.rev.netRecPayableRaw > 0 ? '#16a34a' : (revenueMixData.yesterday.rev.netRecPayableRaw < 0 ? '#ef4444' : '#0f172a') }}>
                        {revenueMixData.yesterday.rev.netRecPayable}
                      </td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: revenueMixData.mtd.rev.netRecPayableRaw > 0 ? '#16a34a' : (revenueMixData.mtd.rev.netRecPayableRaw < 0 ? '#ef4444' : '#0f172a') }}>
                        {revenueMixData.mtd.rev.netRecPayable}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: revenueMixData.ytd.rev.netRecPayableRaw > 0 ? '#16a34a' : (revenueMixData.ytd.rev.netRecPayableRaw < 0 ? '#ef4444' : '#0f172a') }}>
                        {revenueMixData.ytd.rev.netRecPayable}
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                      <td style={{ padding: '7px 0', color: '#0f172a', fontWeight: 700 }}>Gross revenue</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 700, color: '#0f172a' }}>{revenueMixData.yesterday.rev.totalGross}</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 700, color: '#0f172a' }}>{revenueMixData.mtd.rev.totalGross}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>{revenueMixData.ytd.rev.totalGross}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '6px 0', color: '#1e293b', fontWeight: 500 }}>Total charges and commission</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: revenueMixData.yesterday.rev.chargesRaw < 0 ? '#ef4444' : '#0f172a' }}>{revenueMixData.yesterday.rev.charges}</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: revenueMixData.mtd.rev.chargesRaw < 0 ? '#ef4444' : '#0f172a' }}>{revenueMixData.mtd.rev.charges}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: revenueMixData.ytd.rev.chargesRaw < 0 ? '#ef4444' : '#0f172a' }}>{revenueMixData.ytd.rev.charges}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '6px 0', color: '#1e293b', fontWeight: 500 }}>DSM penalty</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: revenueMixData.yesterday.rev.dsmRaw < 0 ? '#ef4444' : '#0f172a' }}>{revenueMixData.yesterday.rev.dsm}</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: revenueMixData.mtd.rev.dsmRaw < 0 ? '#ef4444' : '#0f172a' }}>{revenueMixData.mtd.rev.dsm}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: revenueMixData.ytd.rev.dsmRaw < 0 ? '#ef4444' : '#0f172a' }}>{revenueMixData.ytd.rev.dsm}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f8fafc', background: '#f8fafc' }}>
                      <td style={{ padding: '7px 0', color: '#0f172a', fontWeight: 700 }}>Net revenue post dsm</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 700, color: '#0f172a' }}>{revenueMixData.yesterday.rev.netPostDsm}</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 700, color: '#0f172a' }}>{revenueMixData.mtd.rev.netPostDsm}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>{revenueMixData.ytd.rev.netPostDsm}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '6px 0', color: '#1e293b', fontWeight: 500 }}>REC</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: '#0f172a' }}>{revenueMixData.yesterday.rev.rec || '-'}</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: '#0f172a' }}>{revenueMixData.mtd.rev.rec}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>{revenueMixData.ytd.rev.rec}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Col>

            {/* Per Unit Block: Per unit (₹/kWh) */}
            <Col xs={24} lg={12}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1B4F72', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>
                Per unit (₹/kWh)
              </div>
              <div style={{
                border: SUMMARY_BOX_BORDER,
                borderRadius: 6,
                padding: '8px 14px',
                background: '#fff',
                overflowX: 'auto'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, fontFamily: 'Inter, sans-serif' }}>
                  <thead>
                    <tr style={{ borderBottom: '1.5px solid #cbd5e1' }}>
                      <th style={{ textAlign: 'left', paddingBottom: 6, color: '#64748b', fontWeight: 600, fontSize: 11.5, width: '42%' }}></th>
                      <th style={{ textAlign: 'right', paddingBottom: 6, paddingRight: 12, color: '#64748b', fontWeight: 600, fontSize: 11.5, width: '20%' }}>{genPlfData.yesterdayLabel}</th>
                      <th style={{ textAlign: 'right', paddingBottom: 6, paddingRight: 12, color: '#64748b', fontWeight: 600, fontSize: 11.5, width: '19%' }}>MTD</th>
                      <th style={{ textAlign: 'right', paddingBottom: 6, color: '#64748b', fontWeight: 600, fontSize: 11.5, width: '19%' }}>YTD</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '6px 0', color: '#1e293b', fontWeight: 500 }}>Bilateral</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: '#0f172a' }}>{revenueMixData.yesterday.pu.bilat}</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: '#0f172a' }}>{revenueMixData.mtd.pu.bilat}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>{revenueMixData.ytd.pu.bilat}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '6px 0', color: '#1e293b', fontWeight: 500 }}>Exchange</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: '#0f172a' }}>{revenueMixData.yesterday.pu.exch}</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: '#0f172a' }}>{revenueMixData.mtd.pu.exch}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>{revenueMixData.ytd.pu.exch}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '6px 0', color: '#1e293b', fontWeight: 500 }}>Adjustment due to OI/UI</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: revenueMixData.yesterday.pu.netRecPayableRaw > 0 ? '#16a34a' : (revenueMixData.yesterday.pu.netRecPayableRaw < 0 ? '#ef4444' : '#0f172a') }}>
                        {revenueMixData.yesterday.pu.netRecPayable}
                      </td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: revenueMixData.mtd.pu.netRecPayableRaw > 0 ? '#16a34a' : (revenueMixData.mtd.pu.netRecPayableRaw < 0 ? '#ef4444' : '#0f172a') }}>
                        {revenueMixData.mtd.pu.netRecPayable}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: revenueMixData.ytd.pu.netRecPayableRaw > 0 ? '#16a34a' : (revenueMixData.ytd.pu.netRecPayableRaw < 0 ? '#ef4444' : '#0f172a') }}>
                        {revenueMixData.ytd.pu.netRecPayable}
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                      <td style={{ padding: '7px 0', color: '#0f172a', fontWeight: 700 }}>Gross revenue</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 700, color: '#0f172a' }}>{revenueMixData.yesterday.pu.totalGross}</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 700, color: '#0f172a' }}>{revenueMixData.mtd.pu.totalGross}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>{revenueMixData.ytd.pu.totalGross}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '6px 0', color: '#1e293b', fontWeight: 500 }}>Total charges and commission</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: revenueMixData.yesterday.pu.chargesRaw < 0 ? '#ef4444' : '#0f172a' }}>{revenueMixData.yesterday.pu.charges}</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: revenueMixData.mtd.pu.chargesRaw < 0 ? '#ef4444' : '#0f172a' }}>{revenueMixData.mtd.pu.charges}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: revenueMixData.ytd.pu.chargesRaw < 0 ? '#ef4444' : '#0f172a' }}>{revenueMixData.ytd.pu.charges}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '6px 0', color: '#1e293b', fontWeight: 500 }}>DSM penalty</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: revenueMixData.yesterday.pu.dsmRaw < 0 ? '#ef4444' : '#0f172a' }}>{revenueMixData.yesterday.pu.dsm}</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: revenueMixData.mtd.pu.dsmRaw < 0 ? '#ef4444' : '#0f172a' }}>{revenueMixData.mtd.pu.dsm}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: revenueMixData.ytd.pu.dsmRaw < 0 ? '#ef4444' : '#0f172a' }}>{revenueMixData.ytd.pu.dsm}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f8fafc', background: '#f8fafc' }}>
                      <td style={{ padding: '7px 0', color: '#0f172a', fontWeight: 700 }}>Net revenue post dsm</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 700, color: '#0f172a' }}>{revenueMixData.yesterday.pu.netPostDsm}</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 700, color: '#0f172a' }}>{revenueMixData.mtd.pu.netPostDsm}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>{revenueMixData.ytd.pu.netPostDsm}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '6px 0', color: '#1e293b', fontWeight: 500 }}>REC</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: '#0f172a' }}>{revenueMixData.yesterday.pu.rec || '-'}</td>
                      <td style={{ textAlign: 'right', paddingRight: 12, fontWeight: 600, color: '#0f172a' }}>{revenueMixData.mtd.pu.rec}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>{revenueMixData.ytd.pu.rec}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Col>
          </Row>
        </Card>

        {/* 4th Block: Split into 2 (YTD Waterfall per unit & YTD Charges & Deductions per unit) */}
        <Row gutter={[20, 20]} style={{ marginBottom: 20 }}>
          {/* 1st Part: Waterfall Graph (YTD per unit) */}
          <Col xs={24} lg={12}>
            <Card
              bordered={true}
              headStyle={SUMMARY_CARD_HEAD_STYLE}
              title={<span style={{ fontSize: 14.5, fontWeight: 700, color: '#1B4F72' }}>Consolidated Revenue Waterfall (YTD)</span>}
              style={{
                borderRadius: 8,
                background: '#fff',
                height: '100%',
                border: SUMMARY_BOX_BORDER,
                overflow: 'hidden'
              }}
              bodyStyle={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
            >
              <div style={{ height: 260, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {ytdWaterfallData && ytdWaterfallData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={ytdWaterfallData} margin={{ left: 5, right: 5, bottom: 10 }} barCategoryGap="15%">
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" interval={0} tick={{ fontSize: 10.5, fontWeight: 500 }} />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v) => `₹${Math.round(Number(v))}`}
                        label={{ value: 'Realization (₹/kWh)', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#64748b' } }}
                      />
                      <Tooltip content={<WaterfallPerUnitTooltip />} />
                      <Bar dataKey="displayValue" shape={<WaterfallPerUnitBarShape />} barSize={40}>
                        {ytdWaterfallData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Spin size="small" />
                )}
              </div>

              {ytdWaterfallData && ytdWaterfallData.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 14, marginTop: 12, flexWrap: 'wrap', fontSize: 11, color: '#475569' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: '#0052CC' }} /> Gross
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: ytdWaterfallData.find(item => item.name === 'Net Receivable/Payable')?.color || '#16a34a' }} /> Net Receivable/Payable
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: '#E74C3C' }} /> Charges
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: '#DC2626' }} /> DSM Penalty
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: '#E67E22' }} /> Rev post-DSM
                  </span>
                </div>
              )}
            </Card>
          </Col>

          {/* 2nd Part: Charges & Deductions Pie Chart (YTD per unit) */}
          <Col xs={24} lg={12}>
            <Card
              bordered={true}
              headStyle={SUMMARY_CARD_HEAD_STYLE}
              title={<span style={{ fontSize: 14.5, fontWeight: 700, color: '#1B4F72' }}>Charges & Deductions (YTD)</span>}
              style={{
                borderRadius: 8,
                background: '#fff',
                height: '100%',
                border: SUMMARY_BOX_BORDER,
                overflow: 'hidden'
              }}
              bodyStyle={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
            >
              <div style={{ height: 260, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {ytdChargesData && ytdChargesData.length > 0 ? (
                  <PerUnitChargesDonut
                    data={ytdChargesData}
                    colors={{
                      stu_charges: '#E74C3C',
                      nldc_fees: '#3498DB',
                      trader_comm: '#F39C12',
                      exch_comm: '#16A085',
                      tax: '#8E44AD',
                      dsm_penalty: '#C0392B'
                    }}
                  />
                ) : (
                  <Spin size="small" />
                )}
              </div>

            </Card>
          </Col>
        </Row>

        {/* Month-wise REC (YTD) Line Chart */}
        <Row gutter={[20, 20]} style={{ marginBottom: 20 }}>
          <Col xs={24}>
            <Card
              id="chart-monthwise-rec-ytd"
              bordered={true}
              headStyle={SUMMARY_CARD_HEAD_STYLE}
              style={expandedChartYtdRec ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', border: SUMMARY_BOX_BORDER, overflow: 'hidden' }}
              bodyStyle={{ padding: '20px 24px' }}
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#1B4F72' }}>Month-wise REC (YTD)</span>
                </div>
              }
              extra={
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <Radio.Group value={viewYtdRec} onChange={e => setViewYtdRec(e.target.value)} size="small">
                    <Radio.Button value="chart">Chart</Radio.Button>
                    <Radio.Button value="table">Table</Radio.Button>
                  </Radio.Group>
                  {viewYtdRec === 'chart' ? (
                    <DownloadButton onPng={() => exportCardAsPng('chart-monthwise-rec-ytd', `Monthwise_REC_YTD_${dayjs().format('MMMYYYY')}`)} />
                  ) : (
                    <DownloadButton onCsv={() => exportTableAsCsv(chartYtdRecData, [
                      { title: 'Month', dataIndex: 'monthLabel', key: 'monthLabel' },
                      { title: 'REC Revenue (₹ M)', dataIndex: 'recRevM', key: 'recRevM', render: v => `₹ ${Number(v).toFixed(2)} M` },
                      { title: 'REC Per Unit (₹/kWh)', dataIndex: 'recPU', key: 'recPU', render: v => `₹ ${Number(v).toFixed(2)}/kWh` },
                      { title: 'REC Volume (MU)', dataIndex: 'recVolMU', key: 'recVolMU', render: v => `${Number(v).toFixed(2)} MU` },
                    ], `Monthwise_REC_YTD_${dayjs().format('MMMYYYY')}`)} />
                  )}
                  <Button icon={expandedChartYtdRec ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChartYtdRec(!expandedChartYtdRec)} />
                </div>
              }
            >
              {viewYtdRec === 'chart' ? (
                <div style={{ height: expandedChartYtdRec ? 'calc(100vh - 120px)' : 320, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartYtdRecData} margin={{ left: 25, right: 25, bottom: 25, top: 15 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="monthLabel" tick={{ fontSize: 11, fill: '#475569' }} interval={0} />
                      <YAxis
                        tick={{ fontSize: 11, fill: '#1B4F72' }}
                        tickFormatter={(v) => `₹${Number(v).toFixed(1)}M`}
                        label={{ value: 'REC Revenue (₹ M)', angle: -90, position: 'insideLeft', dx: -10, style: { fontSize: 11, fill: '#1B4F72', fontWeight: 600, textAnchor: 'middle' } }}
                      />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#ffffff', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: 'none', zIndex: 9999 }}
                        formatter={(val, name) => [`₹ ${Number(val).toFixed(2)} M`, name]}
                        labelFormatter={(label) => `Month: ${label}`}
                      />
                      <Legend wrapperStyle={{ paddingTop: 8, fontSize: 11 }} />
                      <Line
                        type="monotone"
                        dataKey="recRevM"
                        name="REC Revenue"
                        stroke="#1B4F72"
                        strokeWidth={2.5}
                        dot={false}
                        activeDot={{ r: 5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <Table
                  dataSource={chartYtdRecData}
                  columns={[
                    { title: 'Month', dataIndex: 'monthLabel', key: 'monthLabel' },
                    { title: 'REC Revenue (₹ M)', dataIndex: 'recRevM', key: 'recRevM', render: v => `₹ ${Number(v).toFixed(2)} M` },
                    { title: 'REC Per Unit (₹/kWh)', dataIndex: 'recPU', key: 'recPU', render: v => `₹ ${Number(v).toFixed(2)}` },
                    { title: 'REC Volume (MU)', dataIndex: 'recVolMU', key: 'recVolMU', render: v => `${Number(v).toFixed(2)} MU` },
                  ]}
                  size="small"
                  scroll={{ x: 'max-content', y: 280 }}
                  pagination={false}
                  rowKey="period"
                />
              )}
            </Card>
          </Col>
        </Row>

        {/* 5th Block: Historical 3-Year & 1-Year Analytics */}
        <div style={{ marginTop: 24, marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1B4F72', marginBottom: 16, borderBottom: '2px solid #e2e8f0', paddingBottom: 8 }}>
            Historical Performance & Trends
          </div>

          <Row gutter={[20, 20]}>
            {/* Chart (a): Month-wise net revenue — plant (last 3 years) */}
            <Col xs={24} lg={12}>
              <Card
                id="chart-monthwise-net-rev"
                bordered={true}
                headStyle={SUMMARY_CARD_HEAD_STYLE}
                style={expandedChart5a ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', height: '100%', border: SUMMARY_BOX_BORDER, overflow: 'hidden' }}
                bodyStyle={{ padding: '20px 24px' }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1B4F72' }}>Month-wise net revenue — plant</span>
                  </div>
                }
                extra={
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <Radio.Group value={view5a} onChange={e => setView5a(e.target.value)} size="small">
                      <Radio.Button value="chart">Chart</Radio.Button>
                      <Radio.Button value="table">Table</Radio.Button>
                    </Radio.Group>
                    {view5a === 'chart' ? (
                      <DownloadButton onPng={() => exportCardAsPng('chart-monthwise-net-rev', `Monthwise_Net_Revenue_3Y_${dayjs().format('MMMYYYY')}`)} />
                    ) : (
                      <DownloadButton onCsv={() => exportTableAsCsv(chart5Data, [
                        { title: 'Month', dataIndex: 'monthLabel', key: 'monthLabel' },
                        { title: 'Net Revenue (₹ M)', dataIndex: 'netRevenueM', key: 'netRevenueM', render: v => `₹ ${Number(v).toFixed(2)} M` },
                        { title: 'Net Revenue (₹)', dataIndex: 'rawRevenue', key: 'rawRevenue', render: v => `₹ ${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` }
                      ], `Monthwise_Net_Revenue_3Y_${dayjs().format('MMMYYYY')}`)} />
                    )}
                    <Button icon={expandedChart5a ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart5a(!expandedChart5a)} />
                  </div>
                }
              >
                {view5a === 'chart' ? (
                  <div style={{ height: expandedChart5a ? 'calc(100vh - 120px)' : 320, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chart5Data} margin={{ left: 10, right: 15, bottom: 25, top: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="monthLabel" tick={{ fontSize: 10, fill: '#475569' }} interval={0} angle={-45} textAnchor="end" height={45} />
                        <YAxis tick={{ fontSize: 11, fill: '#475569' }} tickFormatter={(v) => `₹${Math.round(Number(v))}M`} label={{ value: 'Net Revenue (₹ M)', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#64748b' } }} />
                        <Tooltip contentStyle={{ backgroundColor: '#ffffff', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: 'none', zIndex: 9999 }} formatter={(val) => [`₹ ${Number(val).toFixed(2)} M`, 'Net Revenue']} labelFormatter={(label) => `Month: ${label}`} />
                        <Bar dataKey="netRevenueM" name="Net Revenue" fill="#1B4F72" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <Table dataSource={chart5Data} columns={[
                    { title: 'Month', dataIndex: 'monthLabel', key: 'monthLabel' },
                    { title: 'Net Revenue (₹ M)', dataIndex: 'netRevenueM', key: 'netRevenueM', render: v => `₹ ${Number(v).toFixed(2)} M` },
                    { title: 'Net Revenue (₹)', dataIndex: 'rawRevenue', key: 'rawRevenue', render: v => `₹ ${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` }
                  ]} size="small" scroll={{ x: 'max-content', y: 280 }} pagination={false} rowKey="period" />
                )}
              </Card>
            </Col>

            {/* Chart (b): Month-wise PU — plant (2025, 2026) */}
            <Col xs={24} lg={12}>
              <Card
                id="chart-monthwise-pu-plant"
                bordered={true}
                headStyle={SUMMARY_CARD_HEAD_STYLE}
                style={expandedChart5b ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', height: '100%', border: SUMMARY_BOX_BORDER, overflow: 'hidden' }}
                bodyStyle={{ padding: '20px 24px' }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1B4F72' }}>Month-wise PU — plant</span>
                  </div>
                }
                extra={
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <Radio.Group value={view5b} onChange={e => setView5b(e.target.value)} size="small">
                      <Radio.Button value="chart">Chart</Radio.Button>
                      <Radio.Button value="table">Table</Radio.Button>
                    </Radio.Group>
                    {view5b === 'chart' ? (
                      <DownloadButton onPng={() => exportCardAsPng('chart-monthwise-pu-plant', `Monthwise_PU_Plant_${dayjs().format('MMMYYYY')}`)} />
                    ) : (
                      <DownloadButton onCsv={() => exportTableAsCsv(chartPlantPu3YData, [
                        { title: 'Month', dataIndex: 'month', key: 'month' },
                        { title: '2025 (₹/kWh)', dataIndex: '2025', key: '2025', render: v => v !== null && v !== undefined ? `₹ ${Number(v).toFixed(2)}/kWh` : '-' },
                        { title: '2026 (₹/kWh)', dataIndex: '2026', key: '2026', render: v => v !== null && v !== undefined ? `₹ ${Number(v).toFixed(2)}/kWh` : '-' }
                      ], `Monthwise_PU_Plant_${dayjs().format('MMMYYYY')}`)} />
                    )}
                    <Button icon={expandedChart5b ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart5b(!expandedChart5b)} />
                  </div>
                }
              >
                {view5b === 'chart' ? (
                  <div style={{ height: expandedChart5b ? 'calc(100vh - 120px)' : 320, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartPlantPu3YData} margin={{ left: 10, right: 15, bottom: 25, top: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#475569' }} />
                        <YAxis domain={[0, 'auto']} tick={{ fontSize: 11, fill: '#475569' }} tickFormatter={(v) => `₹${Math.round(Number(v))}`} label={{ value: 'Realization (₹/kWh)', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#64748b' } }} />
                        <Tooltip contentStyle={{ backgroundColor: '#ffffff', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: 'none', zIndex: 9999 }} formatter={(val, name) => [val !== null && val !== undefined ? `₹ ${Number(val).toFixed(2)} / kWh` : '-', name]} labelFormatter={(label) => `Month: ${label}`} />
                        <Legend wrapperStyle={{ paddingTop: 6, fontSize: 11 }} />
                        <Line type="monotone" dataKey="2025" name="2025" stroke="#3B82F6" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} connectNulls={false} />
                        <Line type="monotone" dataKey="2026" name="2026" stroke="#10B981" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} connectNulls={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <Table dataSource={chartPlantPu3YData} columns={[
                    { title: 'Month', dataIndex: 'month', key: 'month' },
                    { title: '2025 (₹/kWh)', dataIndex: '2025', key: '2025', render: v => v !== null && v !== undefined ? `₹ ${Number(v).toFixed(2)}/kWh` : '-' },
                    { title: '2026 (₹/kWh)', dataIndex: '2026', key: '2026', render: v => v !== null && v !== undefined ? `₹ ${Number(v).toFixed(2)}/kWh` : '-' }
                  ]} size="small" scroll={{ x: 'max-content', y: 280 }} pagination={false} rowKey="monthNum" />
                )}
              </Card>
            </Col>

            {/* Chart (c): Month-wise PU — exchange (3-Year Comparison: 2024, 2025, 2026) */}
            <Col xs={24} lg={12}>
              <Card
                id="chart-monthwise-pu-exch"
                bordered={true}
                headStyle={SUMMARY_CARD_HEAD_STYLE}
                style={expandedChart5c ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', height: '100%', border: SUMMARY_BOX_BORDER, overflow: 'hidden' }}
                bodyStyle={{ padding: '20px 24px' }}
                title={
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1B4F72' }}>Month-wise PU — Exchange</span>
                  </div>
                }
                extra={
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <Radio.Group value={view5c} onChange={e => setView5c(e.target.value)} size="small">
                      <Radio.Button value="chart">Chart</Radio.Button>
                      <Radio.Button value="table">Table</Radio.Button>
                    </Radio.Group>
                    {view5c === 'chart' ? (
                      <DownloadButton onPng={() => exportCardAsPng('chart-monthwise-pu-exch', `Monthwise_PU_Exchange_3Y_${dayjs().format('MMMYYYY')}`)} />
                    ) : (
                      <DownloadButton onCsv={() => exportTableAsCsv(chartExchPu3YData, [
                        { title: 'Month', dataIndex: 'month', key: 'month' },
                        { title: '2024 (₹/kWh)', dataIndex: '2024', key: '2024', render: v => v !== null && v !== undefined ? `₹ ${Number(v).toFixed(2)}/kWh` : '-' },
                        { title: '2025 (₹/kWh)', dataIndex: '2025', key: '2025', render: v => v !== null && v !== undefined ? `₹ ${Number(v).toFixed(2)}/kWh` : '-' },
                        { title: '2026 (₹/kWh)', dataIndex: '2026', key: '2026', render: v => v !== null && v !== undefined ? `₹ ${Number(v).toFixed(2)}/kWh` : '-' }
                      ], `Monthwise_PU_Exchange_3Y_${dayjs().format('MMMYYYY')}`)} />
                    )}
                    <Button icon={expandedChart5c ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart5c(!expandedChart5c)} />
                  </div>
                }
              >
                {view5c === 'chart' ? (
                  <div style={{ height: expandedChart5c ? 'calc(100vh - 120px)' : 320, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartExchPu3YData} margin={{ left: 15, right: 15, bottom: 25, top: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#475569' }} />
                        <YAxis domain={['auto', 'auto']} tick={{ fontSize: 11, fill: '#475569' }} tickFormatter={(v) => `₹${Math.round(Number(v))}`} label={{ value: 'Net Realization (₹/kWh)', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#64748b', textAnchor: 'middle' } }} />
                        <Tooltip contentStyle={{ backgroundColor: '#ffffff', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: 'none', zIndex: 9999 }} formatter={(val, name) => [val !== null && val !== undefined ? `₹ ${Number(val).toFixed(2)} / kWh` : '-', name]} labelFormatter={(label) => `Month: ${label}`} />
                        <Legend wrapperStyle={{ paddingTop: 6, fontSize: 11 }} />
                        <Line type="monotone" dataKey="2024" name="2024" stroke="#94A3B8" strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls={false} />
                        <Line type="monotone" dataKey="2025" name="2025" stroke="#8B5CF6" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} connectNulls={false} />
                        <Line type="monotone" dataKey="2026" name="2026" stroke="#EC4899" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} connectNulls={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <Table dataSource={chartExchPu3YData} columns={[
                    { title: 'Month', dataIndex: 'month', key: 'month' },
                    { title: '2024 (₹/kWh)', dataIndex: '2024', key: '2024', render: v => v !== null && v !== undefined ? `₹ ${Number(v).toFixed(2)}/kWh` : '-' },
                    { title: '2025 (₹/kWh)', dataIndex: '2025', key: '2025', render: v => v !== null && v !== undefined ? `₹ ${Number(v).toFixed(2)}/kWh` : '-' },
                    { title: '2026 (₹/kWh)', dataIndex: '2026', key: '2026', render: v => v !== null && v !== undefined ? `₹ ${Number(v).toFixed(2)}/kWh` : '-' }
                  ]} size="small" scroll={{ x: 'max-content', y: 280 }} pagination={false} rowKey="monthNum" />
                )}
              </Card>
            </Col>

            {/* Chart (d): Bilateral vs. exchange volume starting from Apr 2025 (% Bar Graph) */}
            <Col xs={24} lg={12}>
              <Card
                id="chart-bilat-vs-exch-vol"
                bordered={true}
                headStyle={SUMMARY_CARD_HEAD_STYLE}
                style={expandedChart5d ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', height: '100%', border: SUMMARY_BOX_BORDER, overflow: 'hidden' }}
                bodyStyle={{ padding: '20px 24px' }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1B4F72' }}>Bilateral vs. Exchange Volume</span>
                  </div>
                }
                extra={
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <Radio.Group value={view5d} onChange={e => setView5d(e.target.value)} size="small">
                      <Radio.Button value="chart">Chart</Radio.Button>
                      <Radio.Button value="table">Table</Radio.Button>
                    </Radio.Group>
                    {view5d === 'chart' ? (
                      <DownloadButton onPng={() => exportCardAsPng('chart-bilat-vs-exch-vol', `Bilat_vs_Exch_Volume_Share_${dayjs().format('MMMYYYY')}`)} />
                    ) : (
                      <DownloadButton onCsv={() => exportTableAsCsv(chart5dData, [
                        { title: 'Month', dataIndex: 'monthLabel', key: 'monthLabel' },
                        { title: 'Exchange Share (%)', dataIndex: 'exchSharePct', key: 'exchSharePct', render: v => `${Number(v).toFixed(1)}%` },
                        { title: 'Exchange Vol (MU)', dataIndex: 'exchVolMU', key: 'exchVolMU', render: v => `${Number(v).toFixed(2)} MU` },
                        { title: 'Bilateral Vol (MU)', dataIndex: 'bilatVolMU', key: 'bilatVolMU', render: v => `${Number(v).toFixed(2)} MU` },
                        { title: 'Total Vol (MU)', dataIndex: 'totalVolMU', key: 'totalVolMU', render: v => `${Number(v).toFixed(2)} MU` }
                      ], `Bilat_vs_Exch_Volume_Share_${dayjs().format('MMMYYYY')}`)} />
                    )}
                    <Button icon={expandedChart5d ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart5d(!expandedChart5d)} />
                  </div>
                }
              >
                {view5d === 'chart' ? (
                  <div style={{ height: expandedChart5d ? 'calc(100vh - 120px)' : 320, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chart5dData} margin={{ left: 10, right: 15, bottom: 25, top: 25 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="monthLabel" tick={{ fontSize: 10, fill: '#475569' }} interval={0} angle={-45} textAnchor="end" height={45} />
                        <YAxis
                          tick={{ fontSize: 11, fill: '#475569' }}
                          tickFormatter={(v) => `${Math.round(v)}`}
                          label={{ value: 'Volume (MU)', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#64748b' } }}
                        />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#ffffff', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: 'none', zIndex: 9999 }}
                          formatter={(val, name, item) => {
                            const payload = item?.payload || {};
                            if (name === 'Exchange' || name === 'Exchange (MU)') {
                              return [`${Number(val).toFixed(2)} MU (${Number(payload.exchSharePct || 0).toFixed(1)}%)`, 'Exchange'];
                            }
                            if (name === 'Bilateral' || name === 'Bilateral (MU)') {
                              return [`${Number(val).toFixed(2)} MU (${Number(payload.bilatSharePct || 0).toFixed(1)}%)`, 'Bilateral'];
                            }
                            return [`${Number(val).toFixed(2)} MU`, name];
                          }}
                          labelFormatter={(label) => `Month: ${label}`}
                        />
                        <Legend wrapperStyle={{ paddingTop: 6, fontSize: 11 }} />
                        <Bar dataKey="bilatVolMU" name="Bilateral" stackId="volume" fill="#1E40AF" />
                        <Bar dataKey="exchVolMU" name="Exchange" stackId="volume" fill="#EA580C" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <Table dataSource={chart5dData} columns={[
                    { title: 'Month', dataIndex: 'monthLabel', key: 'monthLabel' },
                    { title: 'Exchange Share (%)', dataIndex: 'exchSharePct', key: 'exchSharePct', render: v => `${Number(v).toFixed(1)}%` },
                    { title: 'Exchange Vol (MU)', dataIndex: 'exchVolMU', key: 'exchVolMU', render: v => `${Number(v).toFixed(2)} MU` },
                    { title: 'Bilateral Vol (MU)', dataIndex: 'bilatVolMU', key: 'bilatVolMU', render: v => `${Number(v).toFixed(2)} MU` },
                    { title: 'Total Vol (MU)', dataIndex: 'totalVolMU', key: 'totalVolMU', render: v => `${Number(v).toFixed(2)} MU` }
                  ]} size="small" scroll={{ x: 'max-content', y: 280 }} pagination={false} rowKey="period" />
                )}
              </Card>
            </Col>

            {/* Chart (e): DAM, GDAM and RTM Volume (30 days) */}
            <Col xs={24} lg={24}>
              <Card
                id="chart-dam-gdam-rtm-vol-30d"
                bordered={true}
                headStyle={SUMMARY_CARD_HEAD_STYLE}
                style={expandedChart30dExchVol ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', height: '100%', border: SUMMARY_BOX_BORDER, overflow: 'hidden' }}
                bodyStyle={{ padding: '20px 24px' }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1B4F72' }}>DAM, GDAM and RTM Volume (30 days)</span>
                  </div>
                }
                extra={
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <Radio.Group value={view30dExchVol} onChange={e => setView30dExchVol(e.target.value)} size="small">
                      <Radio.Button value="chart">Chart</Radio.Button>
                      <Radio.Button value="table">Table</Radio.Button>
                    </Radio.Group>
                    {view30dExchVol === 'chart' ? (
                      <DownloadButton onPng={() => exportCardAsPng('chart-dam-gdam-rtm-vol-30d', `DAM_GDAM_RTM_Volume_30D_${dayjs().format('MMMYYYY')}`)} />
                    ) : (
                      <DownloadButton onCsv={() => exportTableAsCsv(chart30dExchVolData, [
                        { title: 'Date', dataIndex: 'dayLabel', key: 'dayLabel' },
                        { title: 'GDAM (MU)', dataIndex: 'gdamMU', key: 'gdamMU', render: v => `${Number(v).toFixed(3)} MU` },
                        { title: 'DAM (MU)', dataIndex: 'damMU', key: 'damMU', render: v => `${Number(v).toFixed(3)} MU` },
                        { title: 'RTM (MU)', dataIndex: 'rtmMU', key: 'rtmMU', render: v => `${Number(v).toFixed(3)} MU` },
                        { title: 'Total Exchange (MU)', dataIndex: 'totalExchMU', key: 'totalExchMU', render: v => `${Number(v).toFixed(3)} MU` }
                      ], `DAM_GDAM_RTM_Volume_30D_${dayjs().format('MMMYYYY')}`)} />
                    )}
                    <Button icon={expandedChart30dExchVol ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart30dExchVol(!expandedChart30dExchVol)} />
                  </div>
                }
              >
                {view30dExchVol === 'chart' ? (
                  <div style={{ height: expandedChart30dExchVol ? 'calc(100vh - 120px)' : 340, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chart30dExchVolData} margin={{ left: 10, right: 25, bottom: 25, top: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="dayLabel" tick={{ fontSize: 10, fill: '#475569' }} angle={-45} textAnchor="end" height={45} />
                        <YAxis tick={{ fontSize: 11, fill: '#475569' }} tickFormatter={(v) => `${Number(v).toFixed(2)}`} label={{ value: 'Volume (MU)', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#64748b' } }} />
                        <Tooltip contentStyle={{ backgroundColor: '#ffffff', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: 'none', zIndex: 9999 }} formatter={(val, name) => [`${Number(val).toFixed(3)} MU`, name]} labelFormatter={(label) => `Date: ${label}`} />
                        <Legend wrapperStyle={{ paddingTop: 6, fontSize: 11 }} />
                        <Bar dataKey="gdamMU" name="GDAM Volume" stackId="1" fill="#2ECC71" />
                        <Bar dataKey="damMU" name="DAM Volume" stackId="1" fill="#3498DB" />
                        <Bar dataKey="rtmMU" name="RTM Volume" stackId="1" fill="#F39C12" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <Table dataSource={chart30dExchVolData} columns={[
                    { title: 'Date', dataIndex: 'dayLabel', key: 'dayLabel' },
                    { title: 'GDAM (MU)', dataIndex: 'gdamMU', key: 'gdamMU', render: v => `${Number(v).toFixed(3)} MU` },
                    { title: 'DAM (MU)', dataIndex: 'damMU', key: 'damMU', render: v => `${Number(v).toFixed(3)} MU` },
                    { title: 'RTM (MU)', dataIndex: 'rtmMU', key: 'rtmMU', render: v => `${Number(v).toFixed(3)} MU` },
                    { title: 'Total Exchange (MU)', dataIndex: 'totalExchMU', key: 'totalExchMU', render: v => `${Number(v).toFixed(3)} MU` }
                  ]} size="small" scroll={{ x: 'max-content', y: 280 }} pagination={false} rowKey="date" />
                )}
              </Card>
            </Col>

            {/* Chart (f): Cleared Volume on Exchange (Yesterday - Time Block Wise Stacked) with Market Price Line */}
            <Col xs={24} lg={24}>
              <Card
                id="chart-yest-exch-cleared-vol-mcp"
                bordered={true}
                headStyle={SUMMARY_CARD_HEAD_STYLE}
                style={expandedChartYestExchBlock ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { borderRadius: 8, background: '#fff', height: '100%', border: SUMMARY_BOX_BORDER, overflow: 'hidden' }}
                bodyStyle={{ padding: '20px 24px' }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1B4F72' }}>
                      Cleared Volume of Exchange Segments & Market Price ({dayjs().subtract(1, 'day').format('DD-MM-YYYY')})
                    </span>
                  </div>
                }
                extra={
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <Radio.Group value={viewYestExchBlock} onChange={e => setViewYestExchBlock(e.target.value)} size="small">
                      <Radio.Button value="chart">Chart</Radio.Button>
                      <Radio.Button value="table">Table</Radio.Button>
                    </Radio.Group>
                    {viewYestExchBlock === 'chart' ? (
                      <DownloadButton onPng={() => exportCardAsPng('chart-yest-exch-cleared-vol-mcp', `Yesterday_Exch_Cleared_Vol_MCP_${dayjs().subtract(1, 'day').format('DDMMYYYY')}`)} />
                    ) : (
                      <DownloadButton onCsv={() => exportTableAsCsv(chartYestExchBlockVolData, [
                        { title: 'Block', dataIndex: 'block', key: 'block' },
                        { title: 'Time', dataIndex: 'time', key: 'time' },
                        { title: 'GDAM Cleared (MW)', dataIndex: 'gdam_cleared', key: 'gdam_cleared', render: v => `${Number(v).toFixed(2)} MW` },
                        { title: 'DAM Cleared (MW)', dataIndex: 'dam_cleared', key: 'dam_cleared', render: v => `${Number(v).toFixed(2)} MW` },
                        { title: 'RTM Cleared (MW)', dataIndex: 'rtm_cleared', key: 'rtm_cleared', render: v => `${Number(v).toFixed(2)} MW` },
                        { title: 'Total Cleared (MW)', dataIndex: 'total_cleared', key: 'total_cleared', render: v => `${Number(v).toFixed(2)} MW` },
                        { title: 'Market Price Wtd Avg (₹/kWh)', dataIndex: 'wtd_mcp', key: 'wtd_mcp', render: v => v != null ? `₹${Number(v).toFixed(2)}/kWh` : '-' },
                        { title: 'Our Realized (PU) (₹/kWh)', dataIndex: 'our_realized', key: 'our_realized', render: v => `₹${Number(v || 0).toFixed(2)}` }
                      ], `Yesterday_Exch_Cleared_Vol_MCP_${dayjs().subtract(1, 'day').format('DDMMYYYY')}`)} />
                    )}
                    <Button icon={expandedChartYestExchBlock ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChartYestExchBlock(!expandedChartYestExchBlock)} />
                  </div>
                }
              >
                {viewYestExchBlock === 'chart' ? (
                  <div style={{ height: expandedChartYestExchBlock ? 'calc(100vh - 120px)' : 360, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={chartYestExchBlockVolData} margin={{ left: 10, right: 30, bottom: 20, top: 15 }}>
                        <defs>
                          <linearGradient id="gradGdamCleared" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#2ECC71" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#2ECC71" stopOpacity={0.3} />
                          </linearGradient>
                          <linearGradient id="gradDamCleared" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3498DB" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#3498DB" stopOpacity={0.3} />
                          </linearGradient>
                          <linearGradient id="gradRtmCleared" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#F39C12" stopOpacity={0.8} />
                            <stop offset="95%" stopColor="#F39C12" stopOpacity={0.3} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={true} stroke="#e2e8f0" />
                        <XAxis
                          dataKey="time"
                          interval={3}
                          height={35}
                          tickLine={true}
                          stroke="#94a3b8"
                          tick={{ fontSize: 10, fill: '#475569', dy: 6 }}
                          tickFormatter={(v) => v}
                        />
                        {/* Left Y-axis: Cleared Volume (MW) */}
                        <YAxis
                          yAxisId="vol"
                          tick={{ fontSize: 11, fill: '#475569' }}
                          tickFormatter={v => `${Number(v).toFixed(1)}`}
                          label={{ value: 'Cleared Volume (MW)', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#64748b' } }}
                        />
                        {/* Right Y-axis: Market Price & Our Realized (₹/kWh) */}
                        <YAxis
                          yAxisId="price"
                          orientation="right"
                          tick={{ fontSize: 11, fill: '#DC2626' }}
                          tickFormatter={v => `₹${Number(v).toFixed(1)}`}
                          domain={[0, 'auto']}
                          label={{ value: 'Price / Realization (₹/kWh)', angle: 90, position: 'insideRight', style: { fontSize: 11, fill: '#DC2626' } }}
                        />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#ffffff', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', border: 'none', zIndex: 9999 }}
                          labelFormatter={(label, payload) => {
                            const fullTime = payload?.[0]?.payload?.timeInterval || label;
                            return `Time: ${fullTime}`;
                          }}
                          formatter={(val, name) => {
                            if (name.includes('Price')) {
                              return [val != null ? `₹${Number(val).toFixed(2)}/kWh` : '-', name];
                            }
                            if (name.includes('Realized') || name.includes('Realised')) {
                              return [`₹${Number(val || 0).toFixed(2)}/kWh`, name];
                            }
                            return [`${Number(val).toFixed(2)} MW`, name];
                          }}
                        />
                        <Legend wrapperStyle={{ paddingTop: 6, fontSize: 11 }} />
                        <Area
                          yAxisId="vol"
                          type="monotone"
                          dataKey="gdam_cleared"
                          name="GDAM Cleared (MW)"
                          stackId="exch_vol"
                          stroke="#2ECC71"
                          fill="url(#gradGdamCleared)"
                          fillOpacity={1}
                        />
                        <Area
                          yAxisId="vol"
                          type="monotone"
                          dataKey="dam_cleared"
                          name="DAM Cleared (MW)"
                          stackId="exch_vol"
                          stroke="#3498DB"
                          fill="url(#gradDamCleared)"
                          fillOpacity={1}
                        />
                        <Area
                          yAxisId="vol"
                          type="monotone"
                          dataKey="rtm_cleared"
                          name="RTM Cleared (MW)"
                          stackId="exch_vol"
                          stroke="#F39C12"
                          fill="url(#gradRtmCleared)"
                          fillOpacity={1}
                        />
                        <Line
                          yAxisId="price"
                          type="monotone"
                          dataKey="wtd_mcp"
                          name="Market Price (Wtd Avg)"
                          stroke="#DC2626"
                          strokeWidth={2.4}
                          dot={false}
                          activeDot={{ r: 5 }}
                          connectNulls
                        />
                        <Line
                          yAxisId="price"
                          type="monotone"
                          dataKey="our_realized"
                          name="Our Realized (PU)"
                          stroke="#4F46E5"
                          strokeWidth={2.2}
                          strokeDasharray="4 4"
                          dot={false}
                          activeDot={{ r: 5 }}
                          connectNulls
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <Table
                    dataSource={chartYestExchBlockVolData}
                    columns={[
                      { title: 'Block', dataIndex: 'block', key: 'block', width: 80 },
                      { title: 'Time', dataIndex: 'time', key: 'time', width: 90 },
                      { title: 'GDAM Cleared (MW)', dataIndex: 'gdam_cleared', key: 'gdam_cleared', render: v => `${Number(v).toFixed(2)} MW` },
                      { title: 'DAM Cleared (MW)', dataIndex: 'dam_cleared', key: 'dam_cleared', render: v => `${Number(v).toFixed(2)} MW` },
                      { title: 'RTM Cleared (MW)', dataIndex: 'rtm_cleared', key: 'rtm_cleared', render: v => `${Number(v).toFixed(2)} MW` },
                      { title: 'Total Cleared (MW)', dataIndex: 'total_cleared', key: 'total_cleared', render: v => <strong>{Number(v).toFixed(2)} MW</strong> },
                      { title: 'Market Price Wtd Avg (₹/kWh)', dataIndex: 'wtd_mcp', key: 'wtd_mcp', render: v => v != null ? `₹${Number(v).toFixed(2)}/kWh` : '-' },
                      { title: 'Our Realized (PU) (₹/kWh)', dataIndex: 'our_realized', key: 'our_realized', render: v => `₹${Number(v || 0).toFixed(2)}` }
                    ]}
                    size="small"
                    scroll={{ x: 'max-content', y: 300 }}
                    pagination={{ pageSize: 24, showSizeChanger: true, pageSizeOptions: ['24', '48', '96'] }}
                    rowKey="block"
                  />
                )}
              </Card>
            </Col>
          </Row>
        </div>



        {/* Contract Portfolio & Validity Periods Timeline */}
        <ContractTimelineChart plantId={apiParamsBase?.plant_id} />
      </div>
    </Spin>
  );
}

export default function RevenueDashboard() {
  const f = useFilters();
  const [activeTab, setActiveTab] = useState('summary');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedChart, setExpandedChart] = useState(null);

  // Viz modes (trend vs pie)
  const [viz1, setViz1] = useState('trend');
  const [viz2, setViz2] = useState('pie');
  const [viz3, setViz3] = useState('pie');

  // View modes
  const [view1, setView1] = useState('chart');
  const [view2, setView2] = useState('chart');
  const [view3, setView3] = useState('chart');
  const [view5, setView5] = useState('chart');
  const [viewTariff, setViewTariff] = useState('chart');
  const [viewExchSegment, setViewExchSegment] = useState('chart');
  const [viewExchSegmentPU, setViewExchSegmentPU] = useState('chart');
  const [viewExchVolMix, setViewExchVolMix] = useState('chart');
  const [viewBilatExchVol, setViewBilatExchVol] = useState('chart');

  // Chart states
  const [metricsTariff, setMetricsTariff] = useState(['gross_tariff', 'net_tariff', 'actual_tariff']);
  const [metricsExchSegment, setMetricsExchSegment] = useState(['gdam_gross_rev', 'dam_gross_rev', 'rtm_gross_rev']);
  const [metricsExchSegmentPU, setMetricsExchSegmentPU] = useState(['gdam_pu', 'dam_pu', 'rtm_pu']);
  const [metricsExchVolMix, setMetricsExchVolMix] = useState(['gdam_vol_mu', 'dam_vol_mu', 'rtm_vol_mu']);
  const [metricsBilatExchVol, setMetricsBilatExchVol] = useState(['bilat_vol_mu', 'exch_vol_mu']);

  // Chart 1 States
  const [metrics1, setMetrics1] = useState(['gross_rev', 'net_pre', 'net_post']);

  // Chart 2 States
  const [stackMode, setStackMode] = useState('absolute');

  // Chart 3 States
  const [contractFilter, setContractFilter] = useState('all');
  const [metrics3, setMetrics3] = useState(['stu_charges', 'nldc_fees', 'trader_comm', 'exch_comm', 'tax', 'rebate']);

  const [availableContracts, setAvailableContracts] = useState([]);
  const [overallTariffs, setOverallTariffs] = useState({ gross: 0, netPre: 0, netPost: 0, actual: 0 });

  const fetchData = () => {
    setLoading(true);
    getRevenueAnalysis(f.apiParams())
      .then(res => {
        const results = res.data.results || [];
        const rawContracts = res.data.contracts || [];
        setAvailableContracts(rawContracts);

        let totalScheduleMw = 0;
        let totalGenerationMw = 0;
        let totalGrossRev = 0;
        let totalNetPre = 0;
        let totalNetPost = 0;

        results.forEach(r => {
          totalScheduleMw += Number(r.total_schedule_mw) || 0;
          totalGenerationMw += Number(r.total_generation_mw) || 0;
          totalGrossRev += Number(r.gross_rev) || 0;
          totalNetPre += Number(r.net_pre) || 0;
          totalNetPost += Number(r.net_post) || 0;
        });

        const grossTariff = totalScheduleMw > 0 ? (totalGrossRev / (totalScheduleMw * 250.0)) : 0;
        const netTariffPre = totalScheduleMw > 0 ? (totalNetPre / (totalScheduleMw * 250.0)) : 0;
        const netTariffPost = totalScheduleMw > 0 ? (totalNetPost / (totalScheduleMw * 250.0)) : 0;
        const actualTariff = totalScheduleMw > 0 ? (totalNetPost / (totalScheduleMw * 250.0)) : 0;

        setOverallTariffs({ gross: grossTariff, netPre: netTariffPre, netPost: netTariffPost, actual: actualTariff });

        setData(aggregateChartData(results, f.granularity, ['rtm_mcp', 'dam_mcp', 'gdam_mcp', 'gross_tariff', 'net_tariff', 'actual_tariff', 'exch_net_rev_per_unit']));
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching revenue dashboard data:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (expandedChart) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [expandedChart]);


  const CONTRACT_PALETTE = ['#3498DB', '#E67E22', '#1ABC9C', '#F39C12', '#2ECC71', '#9B59B6', '#E74C3C', '#16A085', '#D35400', '#2980B9'];

  const kpis = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        gross_rev: '₹ 0.00',
        net_pre: '₹ 0.00',
        net_post: '₹ 0.00',
        total_charges: '₹ 0.00',
        best_contract: { value: '₹ 0.00', name: '-' },
      };
    }

    let totalGross = 0;
    let totalNetPre = 0;
    let totalNetPost = 0;
    let totalCharges = 0;
    const contractTotals = {};

    data.forEach(d => {
      totalGross += Number(d.gross_rev) || 0;
      totalNetPre += Number(d.net_pre) || 0;
      totalNetPost += Number(d.net_post) || 0;
      totalCharges += Number(d.total_charges) || 0;

      availableContracts.forEach(c => {
        const val = Number(d[`net_${c.id}`]) || 0;
        contractTotals[c.id] = (contractTotals[c.id] || 0) + val;
      });
    });

    const formatRupees = (val) => {
      const absVal = Math.abs(val);
      if (absVal >= 100000) {
        return `₹ ${(val / 1000000).toFixed(2)} M`;
      }
      return `₹ ${val.toFixed(2)}`;
    };

    // Find highest contract net revenue dynamically
    let bestContractName = '-';
    let bestContractVal = 0;

    if (availableContracts.length > 0) {
      availableContracts.forEach(c => {
        const total = contractTotals[c.id] || 0;
        if (total > bestContractVal || bestContractName === '-') {
          bestContractVal = total;
          bestContractName = c.name;
        }
      });
    }

    return {
      gross_rev: formatRupees(totalGross),
      net_pre: formatRupees(totalNetPre),
      net_post: formatRupees(totalNetPost),
      total_charges: formatRupees(totalCharges),
      best_contract: { value: formatRupees(bestContractVal), name: bestContractName },
    };
  }, [data, availableContracts]);

  const totalGrossRevenue = useMemo(() => {
    if (!data || data.length === 0) return 0;
    return data.reduce((acc, curr) => acc + (Number(curr.gross_rev) || 0), 0);
  }, [data]);

  const pieData1 = useMemo(() => {
    return metrics1.map(key => {
      let value = 0;
      data.forEach(d => { value += Number(d[key]) || 0; });
      let name = key === 'gross_rev' ? 'Gross Revenue' : key === 'net_pre' ? 'Net Rev Pre-DSM' : 'Net Rev Post-DSM';
      return { name, value, key };
    });
  }, [data, metrics1]);

  const pieData2 = useMemo(() => {
    let exch = 0;
    let dam = 0, gdam = 0, rtm = 0;
    const contractSums = {};
    availableContracts.forEach(c => { contractSums[c.id] = 0; });

    data.forEach(d => {
      exch += Number(d.net_exch) || 0;
      dam += Number(d.dam_gross_rev) || 0;
      gdam += Number(d.gdam_gross_rev) || 0;
      rtm += Number(d.rtm_gross_rev) || 0;
      availableContracts.forEach(c => {
        contractSums[c.id] += Number(d[`net_${c.id}`]) || 0;
      });
    });

    const totalExchGross = dam + gdam + rtm;
    const gdamNetVal = totalExchGross > 0 ? (gdam / totalExchGross) * exch : 0;
    const damNetVal = totalExchGross > 0 ? (dam / totalExchGross) * exch : 0;
    const rtmNetVal = totalExchGross > 0 ? (rtm / totalExchGross) * exch : 0;

    const slices = [
      {
        name: 'Exchange',
        value: exch,
        key: 'net_exch',
        breakdown: { dam: damNetVal, gdam: gdamNetVal, rtm: rtmNetVal }
      }
    ];

    availableContracts.forEach(c => {
      slices.push({
        name: c.name,
        value: contractSums[c.id] || 0,
        key: `net_${c.id}`
      });
    });

    return slices;
  }, [data, availableContracts]);

  const segmentRates = useMemo(() => {
    if (!data || data.length === 0) {
      return { exchNetRev: 0, exchVolume: 0, contractNetRev: 0, contractVolume: 0 };
    }
    let exchNetRev = 0;
    let exchVolume = 0;
    let contractNetRev = 0;
    let contractVolume = 0;
    data.forEach(d => {
      exchNetRev += Number(d.net_exch) || 0;
      exchVolume += (Number(d.gdam_vol_exbus) || 0) + (Number(d.dam_vol_exbus) || 0) + (Number(d.rtm_vol_exbus) || 0);
      if (availableContracts.length > 0) {
        availableContracts.forEach(c => {
          contractNetRev += Number(d[`net_${c.id}`]) || 0;
        });
      } else {
        contractNetRev += (Number(d.net_goa) || 0) + (Number(d.net_nidar) || 0) + (Number(d.net_npcl) || 0);
      }
      contractVolume += Number(d.contract_schedule_vol) || 0;
    });
    return { exchNetRev, exchVolume, contractNetRev, contractVolume };
  }, [data, availableContracts]);

  const exchNetRevPerUnit = useMemo(() => {
    return segmentRates.exchVolume > 0 ? segmentRates.exchNetRev / segmentRates.exchVolume : 0;
  }, [segmentRates]);

  const contractNetRevPerUnit = useMemo(() => {
    return segmentRates.contractVolume > 0 ? segmentRates.contractNetRev / segmentRates.contractVolume : 0;
  }, [segmentRates]);

  const colors1 = { gross_rev: '#0052CC', net_pre: '#27AE60', net_post: '#E74C3C' };
  const colors2 = useMemo(() => {
    const colMap = { net_exch: '#8E44AD' };
    availableContracts.forEach((c, idx) => {
      colMap[`net_${c.id}`] = CONTRACT_PALETTE[idx % CONTRACT_PALETTE.length];
    });
    return colMap;
  }, [availableContracts]);
  const colors3 = { stu_charges: '#E74C3C', nldc_fees: '#3498DB', trader_comm: '#F39C12', exch_comm: '#16A085', tax: '#2980B9', rebate: '#8E44AD' };

  // ------------------------- Chart 1 Configuration -------------------------
  const columns1 = useMemo(() => getTableColumns(f.granularity, [
    ...(metrics1.includes('gross_rev') ? [{ title: 'Gross Revenue', dataIndex: 'gross_rev', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' }] : []),
    ...(metrics1.includes('net_pre') ? [{ title: 'Pre DSM', dataIndex: 'net_pre', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' }] : []),
    ...(metrics1.includes('net_post') ? [{ title: 'Post DSM', dataIndex: 'net_post', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' }] : []),
  ]), [f.granularity, metrics1]);

  // ------------------------- Chart 2 Configuration -------------------------
  const columns2 = useMemo(() => getTableColumns(f.granularity, [
    { title: 'Exchange', dataIndex: 'net_exch', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' },
    { title: 'Total Contracts', dataIndex: 'contracts', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' },
    ...availableContracts.map(c => ({
      title: c.name,
      dataIndex: `net_${c.id}`,
      render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-'
    }))
  ]), [f.granularity, availableContracts]);

  // ------------------------- Tariff Chart Configuration -------------------------
  const columnsTariff = useMemo(() => getTableColumns(f.granularity, [
    ...(metricsTariff.includes('gross_tariff') ? [{ title: 'Gross Realization (₹/kWh)', dataIndex: 'gross_tariff', render: v => v != null && typeof v === 'number' ? `₹ ${v.toFixed(2)}` : v ?? '-' }] : []),
    ...(metricsTariff.includes('net_tariff') ? [{ title: 'Net Realization - Pre-DSM (₹/kWh)', dataIndex: 'net_tariff', render: v => v != null && typeof v === 'number' ? `₹ ${v.toFixed(2)}` : v ?? '-' }] : []),
    ...(metricsTariff.includes('actual_tariff') ? [{ title: 'Net Realization - Post-DSM (₹/kWh)', dataIndex: 'actual_tariff', render: v => v != null && typeof v === 'number' ? `₹ ${v.toFixed(2)}` : v ?? '-' }] : []),
  ]), [f.granularity, metricsTariff]);

  // ------------------------- Exchange Segment Chart Configuration -------------------------
  const columnsExchSegment = useMemo(() => getTableColumns(f.granularity, [
    ...(metricsExchSegment.includes('gdam_gross_rev') ? [{ title: 'GDAM Gross Revenue (₹)', dataIndex: 'gdam_gross_rev', render: v => v != null && typeof v === 'number' ? `₹ ${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : v ?? '-' }] : []),
    ...(metricsExchSegment.includes('dam_gross_rev') ? [{ title: 'DAM Gross Revenue (₹)', dataIndex: 'dam_gross_rev', render: v => v != null && typeof v === 'number' ? `₹ ${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : v ?? '-' }] : []),
    ...(metricsExchSegment.includes('rtm_gross_rev') ? [{ title: 'RTM Gross Revenue (₹)', dataIndex: 'rtm_gross_rev', render: v => v != null && typeof v === 'number' ? `₹ ${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : v ?? '-' }] : []),
  ]), [f.granularity, metricsExchSegment]);

  // ------------------------- Chart 3 Configuration -------------------------
  const chart3Data = useMemo(() => {
    return data.map(d => {
      if (contractFilter === 'exch') {
        return {
          period: d.period,
          date: d.date,
          block_number: d.block_number,
          stu_charges: Number(d.stu_charges_exch) || 0,
          nldc_fees: Number(d.nldc_fees_exch) || 0,
          trader_comm: Number(d.trader_comm_exch) || 0,
          exch_comm: Number(d.exch_comm_exch) || 0,
          tax: Number(d.tax_exch) || 0,
          rebate: Number(d.rebate_exch) || 0,
        };
      }

      if (contractFilter !== 'all') {
        // Specific contract selected: contractFilter is contract id (e.g. '1', '4', etc.)
        const cid = contractFilter;
        return {
          period: d.period,
          date: d.date,
          block_number: d.block_number,
          stu_charges: Number(d[`stu_charges_${cid}`]) || 0,
          nldc_fees: Number(d[`nldc_fees_${cid}`]) || 0,
          trader_comm: Number(d[`trader_comm_${cid}`]) || 0,
          exch_comm: Number(d[`exch_comm_${cid}`]) || 0,
          tax: Number(d[`tax_${cid}`]) || 0,
          rebate: Number(d[`rebate_${cid}`]) || 0,
        };
      }

      // 'all' selected: sum exchange + all available contracts
      let stu = Number(d.stu_charges_exch) || 0;
      let nldc = Number(d.nldc_fees_exch) || 0;
      let trader = Number(d.trader_comm_exch) || 0;
      let exch_comm = Number(d.exch_comm_exch) || 0;
      let tax = Number(d.tax_exch) || 0;
      let rebate = Number(d.rebate_exch) || 0;

      if (availableContracts.length > 0) {
        availableContracts.forEach(c => {
          stu += Number(d[`stu_charges_${c.id}`]) || 0;
          nldc += Number(d[`nldc_fees_${c.id}`]) || 0;
          trader += Number(d[`trader_comm_${c.id}`]) || 0;
          exch_comm += Number(d[`exch_comm_${c.id}`]) || 0;
          tax += Number(d[`tax_${c.id}`]) || 0;
          rebate += Number(d[`rebate_${c.id}`]) || 0;
        });
      } else {
        stu += (Number(d.stu_charges_goa) || 0) + (Number(d.stu_charges_nidar) || 0) + (Number(d.stu_charges_npcl) || 0);
        nldc += (Number(d.nldc_fees_goa) || 0) + (Number(d.nldc_fees_nidar) || 0) + (Number(d.nldc_fees_npcl) || 0);
        trader += (Number(d.trader_comm_goa) || 0) + (Number(d.trader_comm_nidar) || 0) + (Number(d.trader_comm_npcl) || 0);
        exch_comm += (Number(d.exch_comm_goa) || 0) + (Number(d.exch_comm_nidar) || 0) + (Number(d.exch_comm_npcl) || 0);
        tax += (Number(d.tax_goa) || 0) + (Number(d.tax_nidar) || 0) + (Number(d.tax_npcl) || 0);
        rebate += (Number(d.rebate_goa) || 0) + (Number(d.rebate_nidar) || 0) + (Number(d.rebate_npcl) || 0);
      }

      return {
        period: d.period,
        date: d.date,
        block_number: d.block_number,
        stu_charges: stu,
        nldc_fees: nldc,
        trader_comm: trader,
        exch_comm: exch_comm,
        tax: tax,
        rebate: rebate,
      };
    });
  }, [data, contractFilter, availableContracts]);

  const pieData3 = useMemo(() => {
    return metrics3.map(key => {
      let value = 0;
      chart3Data.forEach(d => { value += Number(d[key]) || 0; });
      const labels = { stu_charges: 'STU Charges', nldc_fees: 'Scheduling Charges', trader_comm: "Trader's Comm.", exch_comm: 'Exchange Comm.', tax: 'Tax', rebate: 'Rebate' };
      return { name: labels[key], value, key };
    });
  }, [chart3Data, metrics3]);

  const totalVolume3 = useMemo(() => {
    if (!data || data.length === 0) return 0;
    let vol = 0;
    data.forEach(d => {
      if (contractFilter === 'all') {
        const exch = (Number(d.gdam_vol_exbus) || 0) + (Number(d.dam_vol_exbus) || 0) + (Number(d.rtm_vol_exbus) || 0);
        const contracts = Number(d.contract_schedule_vol) || 0;
        vol += exch + contracts;
      } else if (contractFilter === 'exch') {
        vol += (Number(d.gdam_vol_exbus) || 0) + (Number(d.dam_vol_exbus) || 0) + (Number(d.rtm_vol_exbus) || 0);
      } else {
        // Specific contract selected
        vol += Number(d[`schedule_vol_${contractFilter}`]) || 0;
      }
    });
    return vol;
  }, [data, contractFilter]);

  const columns3 = useMemo(() => getTableColumns(f.granularity, [
    ...(metrics3.includes('stu_charges') ? [{ title: 'STU Charges', dataIndex: 'stu_charges', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' }] : []),
    ...(metrics3.includes('nldc_fees') ? [{ title: 'Scheduling Charges', dataIndex: 'nldc_fees', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' }] : []),
    ...(metrics3.includes('trader_comm') ? [{ title: "Trader's Comm.", dataIndex: 'trader_comm', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' }] : []),
    ...(metrics3.includes('exch_comm') ? [{ title: 'Exchange Comm.', dataIndex: 'exch_comm', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' }] : []),
    ...(metrics3.includes('tax') ? [{ title: 'Tax', dataIndex: 'tax', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' }] : []),
    ...(metrics3.includes('rebate') ? [{ title: 'Rebate', dataIndex: 'rebate', render: v => v != null && typeof v === 'number' ? v.toFixed(2) : v ?? '-' }] : []),
  ]), [f.granularity, metrics3]);

  const chart2Data = useMemo(() => {
    return data.map(d => {
      const net_exch = Number(d.net_exch) || 0;
      let contracts = 0;
      if (availableContracts.length > 0) {
        availableContracts.forEach(c => {
          contracts += Number(d[`net_${c.id}`]) || 0;
        });
      } else {
        contracts = (Number(d.net_goa) || 0) + (Number(d.net_nidar) || 0) + (Number(d.net_npcl) || 0);
      }
      return {
        ...d,
        net_exch,
        contracts
      };
    });
  }, [data, availableContracts]);

  const analysisDataFormatted = useMemo(() => {
    return data.map(d => {
      const gdamVol = Number(d.gdam_vol_exbus) || 0;
      const damVol = Number(d.dam_vol_exbus) || 0;
      const rtmVol = Number(d.rtm_vol_exbus) || 0;

      const gdamRev = Number(d.gdam_gross_rev) || 0;
      const damRev = Number(d.dam_gross_rev) || 0;
      const rtmRev = Number(d.rtm_gross_rev) || 0;

      const gdam_pu = gdamVol > 0 ? gdamRev / gdamVol : 0;
      const dam_pu = damVol > 0 ? damRev / damVol : 0;
      const rtm_pu = rtmVol > 0 ? rtmRev / rtmVol : 0;

      const bilatVol = Number(d.contract_schedule_vol) || 0;
      const exchVol = gdamVol + damVol + rtmVol;

      // Strictly in MU (1 MU = 1,000,000 kWh)
      const gdam_vol_mu = gdamVol / 1000000.0;
      const dam_vol_mu = damVol / 1000000.0;
      const rtm_vol_mu = rtmVol / 1000000.0;

      const bilat_vol_mu = bilatVol / 1000000.0;
      const exch_vol_mu = exchVol / 1000000.0;

      return {
        ...d,
        gdam_pu,
        dam_pu,
        rtm_pu,
        gdam_vol_kwh: gdamVol,
        dam_vol_kwh: damVol,
        rtm_vol_kwh: rtmVol,
        bilat_vol_kwh: bilatVol,
        exch_vol_kwh: exchVol,
        gdam_vol_mu,
        dam_vol_mu,
        rtm_vol_mu,
        bilat_vol_mu,
        exch_vol_mu,
        total_vol_mu: bilat_vol_mu + exch_vol_mu
      };
    });
  }, [data]);

  const columnsExchSegmentPU = useMemo(() => getTableColumns(f.granularity, [
    ...(metricsExchSegmentPU.includes('gdam_pu') ? [{ title: 'GDAM Realization (₹/kWh)', dataIndex: 'gdam_pu', render: v => v != null && typeof v === 'number' ? `₹ ${v.toFixed(2)}` : v ?? '-' }] : []),
    ...(metricsExchSegmentPU.includes('dam_pu') ? [{ title: 'DAM Realization (₹/kWh)', dataIndex: 'dam_pu', render: v => v != null && typeof v === 'number' ? `₹ ${v.toFixed(2)}` : v ?? '-' }] : []),
    ...(metricsExchSegmentPU.includes('rtm_pu') ? [{ title: 'RTM Realization (₹/kWh)', dataIndex: 'rtm_pu', render: v => v != null && typeof v === 'number' ? `₹ ${v.toFixed(2)}` : v ?? '-' }] : []),
  ]), [f.granularity, metricsExchSegmentPU]);

  const columnsExchVolMix = useMemo(() => getTableColumns(f.granularity, [
    ...(metricsExchVolMix.includes('gdam_vol_mu') ? [{
      title: 'GDAM Volume (MU)',
      dataIndex: 'gdam_vol_mu',
      render: (v, row) => `${(Number(v ?? (Number(row?.gdam_vol_exbus) || 0) / 1000000.0) || 0).toFixed(3)} MU`
    }] : []),
    ...(metricsExchVolMix.includes('dam_vol_mu') ? [{
      title: 'DAM Volume (MU)',
      dataIndex: 'dam_vol_mu',
      render: (v, row) => `${(Number(v ?? (Number(row?.dam_vol_exbus) || 0) / 1000000.0) || 0).toFixed(3)} MU`
    }] : []),
    ...(metricsExchVolMix.includes('rtm_vol_mu') ? [{
      title: 'RTM Volume (MU)',
      dataIndex: 'rtm_vol_mu',
      render: (v, row) => `${(Number(v ?? (Number(row?.rtm_vol_exbus) || 0) / 1000000.0) || 0).toFixed(3)} MU`
    }] : []),
  ]), [f.granularity, metricsExchVolMix]);

  const columnsBilatExchVol = useMemo(() => getTableColumns(f.granularity, [
    ...(metricsBilatExchVol.includes('bilat_vol_mu') ? [{
      title: 'Bilateral Volume (MU)',
      dataIndex: 'bilat_vol_mu',
      render: (v, row) => `${(Number(v ?? (Number(row?.contract_schedule_vol) || 0) / 1000000.0) || 0).toFixed(3)} MU`
    }] : []),
    ...(metricsBilatExchVol.includes('exch_vol_mu') ? [{
      title: 'Exchange Volume (MU)',
      dataIndex: 'exch_vol_mu',
      render: (v, row) => `${(Number(v ?? (((Number(row?.gdam_vol_exbus) || 0) + (Number(row?.dam_vol_exbus) || 0) + (Number(row?.rtm_vol_exbus) || 0)) / 1000000.0)) || 0).toFixed(3)} MU`
    }] : []),
  ]), [f.granularity, metricsBilatExchVol]);

  const tableData1 = useMemo(() => formatTableData(data, f.granularity), [data, f.granularity]);
  const tableData2 = useMemo(() => formatTableData(chart2Data, f.granularity), [chart2Data, f.granularity]);
  const tableData3 = useMemo(() => formatTableData(chart3Data, f.granularity), [chart3Data, f.granularity]);
  const tableDataAnalysis = useMemo(() => formatTableData(analysisDataFormatted, f.granularity), [analysisDataFormatted, f.granularity]);

  const waterfallData = useMemo(() => {
    if (!data || data.length === 0) return [];

    let totalGross = 0;
    let totalNetPre = 0;
    let totalNetPost = 0;
    let totalCharges = 0;
    let totalExchGross = 0;
    let totalContractGross = 0;

    data.forEach(d => {
      totalGross += Number(d.gross_rev) || 0;
      totalNetPre += Number(d.net_pre) || 0;
      totalNetPost += Number(d.net_post) || 0;
      totalCharges += Number(d.total_charges) || 0;
      totalExchGross += Number(d.exchange_gross_rev) || 0;
      totalContractGross += Number(d.contract_gross_rev) || 0;
    });

    const dsmVal = totalNetPost - totalNetPre;

    return [
      {
        name: 'Gross revenue',
        displayValue: [0, totalGross],
        color: '#0052CC',
        raw: totalGross,
        isGross: true,
        exchGross: totalExchGross,
        contractGross: totalContractGross
      },
      {
        name: 'Charges',
        displayValue: [totalNetPre, totalGross],
        color: '#E74C3C',
        raw: -totalCharges
      },
      {
        name: 'DSM',
        displayValue: dsmVal >= 0 ? [totalNetPre, totalNetPost] : [totalNetPost, totalNetPre],
        color: dsmVal >= 0 ? '#558B2F' : '#E74C3C',
        raw: dsmVal
      },
      {
        name: 'Revenue post-DSM',
        displayValue: [0, totalNetPost],
        color: '#E67E22',
        raw: totalNetPost
      }
    ];
  }, [data]);

  const columns5 = [
    { title: 'Step', dataIndex: 'name', key: 'name' },
    { title: 'Value (₹)', dataIndex: 'raw', key: 'raw', render: v => `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` }
  ];

  const tableData5 = useMemo(() => {
    if (waterfallData.length === 0) return [];
    const grossItem = waterfallData[0];
    return [
      {
        ...grossItem,
        key: 'gross',
        children: [
          {
            name: 'Exchange Gross Revenue',
            raw: grossItem.exchGross,
            key: 'gross_exch'
          },
          {
            name: 'Contracts Gross Revenue',
            raw: grossItem.contractGross,
            key: 'gross_contract'
          }
        ]
      },
      ...waterfallData.slice(1).map((item, idx) => ({
        ...item,
        key: `step_${idx + 1}`
      }))
    ];
  }, [waterfallData]);

  const isHourlyOr15Min = f.granularity === 'hourly' || f.granularity === '15min' || f.granularity === '15min-raw';
  const axisInterval = f.granularity === '15min' ? 3 : (f.granularity === '15min-raw' ? 2 : 0);
  const axisTickProps = isHourlyOr15Min
    ? { fontSize: 10, angle: 0, textAnchor: 'middle', dy: 10 }
    : { fontSize: 11, angle: -45, textAnchor: 'end', dy: 15 };

  const brushProps = {};
  const formatTick = v => {
    if (!v) return '';
    if (f.granularity === '15min-raw') {
      if (typeof v === 'string' && v.includes(' ')) {
        return v.split(' ')[1];
      }
      return v;
    }
    if (f.granularity === 'hourly' || f.granularity === '15min') return v;
    const d = dayjs(v);
    if (!d.isValid()) return v;
    if (f.granularity === 'monthly') return d.format('MMM-YY');
    if (f.granularity === 'yearly') return d.format('YYYY');
    return d.format('DD-MMM');
  };

  return (
    <div id="dashboard-revenue" style={{ display: 'flex', minHeight: '100vh' }}>
      <div style={{ flex: 1, padding: '12px 24px 24px', overflowX: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>Revenue Analysis</Typography.Title>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
          {
            key: 'summary',
            label: 'Summary',
            children: <RevenueSummaryTab apiParamsBase={f.apiParams()} />
          },
          {
            key: 'analysis',
            label: 'Analysis',
            children: (
              <Spin spinning={loading}>
                {/* Chart 2: Revenue Trend */}
                <Card id="chart-rev-trend"
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span>Revenue Trend</span>
                      {/* <Button type="text" size="small" icon={<LeftOutlined />} onClick={() => setViz1('trend')} disabled={viz1 === 'trend'} />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>{viz1 === 'trend' ? 'Trend Chart' : 'Pie Chart'}</Typography.Text>
                <Button type="text" size="small" icon={<RightOutlined />} onClick={() => setViz1('pie')} disabled={viz1 === 'pie'} /> */}
                    </div>
                  }
                  size="small" style={expandedChart === 'chart1' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                  extra={
                    <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                      <Checkbox.Group
                        value={metrics1}
                        onChange={setMetrics1}
                        style={{ display: 'flex', gap: 10 }}
                        options={[
                          { label: 'Gross Revenue', value: 'gross_rev' },
                          { label: 'Pre DSM', value: 'net_pre' },
                          { label: 'Post DSM', value: 'net_post' },
                        ]}
                      />
                      <Radio.Group value={view1} onChange={e => setView1(e.target.value)} size="small">
                        <Radio.Button value="chart">Chart</Radio.Button>
                        <Radio.Button value="table">Table</Radio.Button>
                      </Radio.Group>
                      {view1 === 'chart' ? (
                        <DownloadButton onPng={() => exportCardAsPng('chart-rev-trend', `Revenue_Trend_${dayjs().format('MMMYYYY')}`)} />
                      ) : (
                        <DownloadButton onCsv={() => exportTableAsCsv(tableData1, columns1, `Revenue_Trend_${dayjs().format('MMMYYYY')}`)} />
                      )}
                      <Button icon={expandedChart === 'chart1' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart1' ? null : 'chart1')} />
                    </div>
                  }>
                  {view1 === 'chart' ? (
                    <div style={{ height: expandedChart === 'chart1' ? 'calc(100vh - 120px)' : 340 }}>
                      {viz1 === 'pie' ? (
                        <DonutChartWithLegend data={pieData1} colors={colors1} title="Total Revenue" />
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={data} margin={{ left: 10, right: 10, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" interval={axisInterval} height={80} tick={axisTickProps} tickFormatter={formatTick} />
                            <YAxis tick={{ fontSize: 11 }} tickFormatter={formatCurrencyKM} label={{ value: 'Revenue (₹)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                            <Tooltip itemSorter={tooltipSorter} shared={false} labelFormatter={(label) => {
                              if (f.granularity === '15min-raw') {
                                const d = dayjs(label);
                                return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                              }
                              if (isHourlyOr15Min) return `Time: ${label}${f.granularity === 'hourly' ? ':00' : ''}`;
                              const d = dayjs(label);
                              if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                              if (f.granularity === 'yearly') return d.format('YYYY');
                              return d.format('DD MMM, YYYY');
                            }} formatter={(v, n) => [formatTooltipValue(v, 0), n]} />
                            <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 30 }} />
                            <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />
                            {metrics1.includes('gross_rev') && <Line type="monotone" dataKey="gross_rev" name="Gross Revenue" stroke="#0052CC" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                            {metrics1.includes('net_pre') && <Line type="monotone" dataKey="net_pre" name="Pre DSM" stroke="#27AE60" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                            {metrics1.includes('net_post') && <Line type="monotone" dataKey="net_post" name="Post DSM" stroke="#E74C3C" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                          </ComposedChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  ) : (
                    <Table dataSource={tableData1} columns={columns1} size="small" scroll={{ x: 'max-content', y: 280 }} pagination={false} rowKey="period" />
                  )}
                </Card>

                {/* Chart 3: Revenue / Unit */}
                <Card id="chart-rev-unit"
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span>Revenue / Unit</span>
                    </div>
                  }
                  size="small" style={expandedChart === 'chart_tariff' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                  extra={
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <Select mode="multiple" size="small" value={metricsTariff} onChange={setMetricsTariff} style={{ width: 220 }} maxTagCount={1}
                        options={[
                          { label: 'Gross Realization', value: 'gross_tariff' },
                          { label: 'Net Realization Pre-DSM', value: 'net_tariff' },
                          { label: 'Net Realization Post-DSM', value: 'actual_tariff' },
                        ]} />
                      <Radio.Group value={viewTariff} onChange={e => setViewTariff(e.target.value)} size="small">
                        <Radio.Button value="chart">Chart</Radio.Button>
                        <Radio.Button value="table">Table</Radio.Button>
                      </Radio.Group>
                      {viewTariff === 'chart' ? (
                        <DownloadButton onPng={() => exportCardAsPng('chart-rev-unit', `Revenue_Unit_${dayjs().format('MMMYYYY')}`)} />
                      ) : (
                        <DownloadButton onCsv={() => exportTableAsCsv(tableData1, columnsTariff, `Revenue_Unit_${dayjs().format('MMMYYYY')}`)} />
                      )}
                      <Button icon={expandedChart === 'chart_tariff' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart_tariff' ? null : 'chart_tariff')} />
                    </div>
                  }>
                  {viewTariff === 'chart' ? (
                    <div style={{ height: expandedChart === 'chart_tariff' ? 'calc(100vh - 120px)' : 400 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={data} margin={{ left: 10, right: 10, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="period" interval={axisInterval} height={80} tick={axisTickProps} tickFormatter={formatTick} />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={(tick) => `₹${Number(tick).toFixed(2)}`} label={{ value: 'Price / Unit (₹/kWh)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                          <Tooltip itemSorter={tooltipSorter} shared={false} labelFormatter={(label) => {
                            if (f.granularity === '15min-raw') {
                              const d = dayjs(label);
                              return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                            }
                            if (isHourlyOr15Min) return `Time: ${label}${f.granularity === 'hourly' ? ':00' : ''}`;
                            const d = dayjs(label);
                            if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                            if (f.granularity === 'yearly') return d.format('YYYY');
                            return d.format('DD MMM, YYYY');
                          }} formatter={(v, n) => [`₹${Number(v).toFixed(2)}/kWh`, n]} />
                          <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 30 }} />
                          <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />
                          {metricsTariff.includes('gross_tariff') && <Line type="monotone" dataKey="gross_tariff" name="Gross Realization" stroke="#0052CC" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                          {metricsTariff.includes('net_tariff') && <Line type="monotone" dataKey="net_tariff" name="Net Realization - Pre-DSM" stroke="#27AE60" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                          {metricsTariff.includes('actual_tariff') && <Line type="monotone" dataKey="actual_tariff" name="Net Realization - Post-DSM" stroke="#E74C3C" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <Table dataSource={tableData1} columns={columnsTariff} size="small" scroll={{ x: 'max-content', y: 280 }} pagination={false} rowKey="period" />
                  )}
                </Card>

                {/* Chart: Exchange Segment Revenue Breakdown */}
                <Card id="chart-exch-rev-breakdown"
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span>Exchange Revenue Breakdown</span>
                    </div>
                  }
                  size="small" style={expandedChart === 'chart_exch_segment' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                  extra={
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <Select mode="multiple" size="small" value={metricsExchSegment} onChange={setMetricsExchSegment} style={{ width: 220 }} maxTagCount={1}
                        options={[
                          { label: 'GDAM', value: 'gdam_gross_rev' },
                          { label: 'DAM', value: 'dam_gross_rev' },
                          { label: 'RTM', value: 'rtm_gross_rev' },
                        ]} />
                      <Radio.Group value={viewExchSegment} onChange={e => setViewExchSegment(e.target.value)} size="small">
                        <Radio.Button value="chart">Chart</Radio.Button>
                        <Radio.Button value="table">Table</Radio.Button>
                      </Radio.Group>
                      {viewExchSegment === 'chart' ? (
                        <DownloadButton onPng={() => exportCardAsPng('chart-exch-rev-breakdown', `Exchange_Revenue_Breakdown_${dayjs().format('MMMYYYY')}`)} />
                      ) : (
                        <DownloadButton onCsv={() => exportTableAsCsv(tableData1, columnsExchSegment, `Exchange_Revenue_Breakdown_${dayjs().format('MMMYYYY')}`)} />
                      )}
                      <Button icon={expandedChart === 'chart_exch_segment' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart_exch_segment' ? null : 'chart_exch_segment')} />
                    </div>
                  }>
                  {viewExchSegment === 'chart' ? (
                    <div style={{ height: expandedChart === 'chart_exch_segment' ? 'calc(100vh - 120px)' : 400 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={data} margin={{ left: 10, right: 10, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="period" interval={axisInterval} height={80} tick={axisTickProps} tickFormatter={formatTick} />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={formatCurrencyKM} label={{ value: 'Revenue (₹)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                          <Tooltip itemSorter={tooltipSorter} shared={false} labelFormatter={(label) => {
                            if (f.granularity === '15min-raw') {
                              const d = dayjs(label);
                              return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                            }
                            if (isHourlyOr15Min) return `Time: ${label}${f.granularity === 'hourly' ? ':00' : ''}`;
                            const d = dayjs(label);
                            if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                            if (f.granularity === 'yearly') return d.format('YYYY');
                            return d.format('DD MMM, YYYY');
                          }} formatter={(v, n) => [formatTooltipValue(v, 0), n]} />
                          <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 30 }} />
                          <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />
                          {metricsExchSegment.includes('gdam_gross_rev') && <Line type="monotone" dataKey="gdam_gross_rev" name="GDAM Gross Revenue" stroke="#2ECC71" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                          {metricsExchSegment.includes('dam_gross_rev') && <Line type="monotone" dataKey="dam_gross_rev" name="DAM Gross Revenue" stroke="#E67E22" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                          {metricsExchSegment.includes('rtm_gross_rev') && <Line type="monotone" dataKey="rtm_gross_rev" name="RTM Gross Revenue" stroke="#3498DB" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <Table dataSource={tableData1} columns={columnsExchSegment} size="small" scroll={{ x: 'max-content', y: 280 }} pagination={false} rowKey="period" />
                  )}
                </Card>

                {/* Chart: Exchange Revenue Breakdown (Per Unit) */}
                <Card id="chart-exch-rev-breakdown-pu"
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span>Exchange Revenue Breakdown (Per Unit)</span>
                    </div>
                  }
                  size="small" style={expandedChart === 'chart_exch_segment_pu' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                  extra={
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <Select mode="multiple" size="small" value={metricsExchSegmentPU} onChange={setMetricsExchSegmentPU} style={{ width: 220 }} maxTagCount={1}
                        options={[
                          { label: 'GDAM', value: 'gdam_pu' },
                          { label: 'DAM', value: 'dam_pu' },
                          { label: 'RTM', value: 'rtm_pu' },
                        ]} />
                      <Radio.Group value={viewExchSegmentPU} onChange={e => setViewExchSegmentPU(e.target.value)} size="small">
                        <Radio.Button value="chart">Chart</Radio.Button>
                        <Radio.Button value="table">Table</Radio.Button>
                      </Radio.Group>
                      {viewExchSegmentPU === 'chart' ? (
                        <DownloadButton onPng={() => exportCardAsPng('chart-exch-rev-breakdown-pu', `Exchange_Revenue_Breakdown_PU_${dayjs().format('MMMYYYY')}`)} />
                      ) : (
                        <DownloadButton onCsv={() => exportTableAsCsv(tableDataAnalysis, columnsExchSegmentPU, `Exchange_Revenue_Breakdown_PU_${dayjs().format('MMMYYYY')}`)} />
                      )}
                      <Button icon={expandedChart === 'chart_exch_segment_pu' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart_exch_segment_pu' ? null : 'chart_exch_segment_pu')} />
                    </div>
                  }>
                  {viewExchSegmentPU === 'chart' ? (
                    <div style={{ height: expandedChart === 'chart_exch_segment_pu' ? 'calc(100vh - 120px)' : 400 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={analysisDataFormatted} margin={{ left: 10, right: 10, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="period" interval={axisInterval} height={80} tick={axisTickProps} tickFormatter={formatTick} />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${Number(v).toFixed(2)}`} label={{ value: 'Price / Unit (₹/kWh)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                          <Tooltip itemSorter={tooltipSorter} shared={false} labelFormatter={(label) => {
                            if (f.granularity === '15min-raw') {
                              const d = dayjs(label);
                              return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                            }
                            if (isHourlyOr15Min) return `Time: ${label}${f.granularity === 'hourly' ? ':00' : ''}`;
                            const d = dayjs(label);
                            if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                            if (f.granularity === 'yearly') return d.format('YYYY');
                            return d.format('DD MMM, YYYY');
                          }} formatter={(v, n) => [`₹${Number(v).toFixed(2)}/kWh`, n]} />
                          <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 30 }} />
                          <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />
                          {metricsExchSegmentPU.includes('gdam_pu') && <Line type="monotone" dataKey="gdam_pu" name="GDAM Realization" stroke="#2ECC71" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                          {metricsExchSegmentPU.includes('dam_pu') && <Line type="monotone" dataKey="dam_pu" name="DAM Realization" stroke="#E67E22" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                          {metricsExchSegmentPU.includes('rtm_pu') && <Line type="monotone" dataKey="rtm_pu" name="RTM Realization" stroke="#3498DB" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <Table dataSource={tableDataAnalysis} columns={columnsExchSegmentPU} size="small" scroll={{ x: 'max-content', y: 280 }} pagination={false} rowKey="period" />
                  )}
                </Card>

                {/* Chart: Exchange Volume Mix (Stacked Area) */}
                <Card id="chart-exch-vol-mix"
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span>Exchange Volume Mix</span>
                    </div>
                  }
                  size="small" style={expandedChart === 'chart_exch_vol_mix' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                  extra={
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <Select mode="multiple" size="small" value={metricsExchVolMix} onChange={setMetricsExchVolMix} style={{ width: 220 }} maxTagCount={1}
                        options={[
                          { label: 'GDAM Volume', value: 'gdam_vol_mu' },
                          { label: 'DAM Volume', value: 'dam_vol_mu' },
                          { label: 'RTM Volume', value: 'rtm_vol_mu' },
                        ]} />
                      <Radio.Group value={viewExchVolMix} onChange={e => setViewExchVolMix(e.target.value)} size="small">
                        <Radio.Button value="chart">Chart</Radio.Button>
                        <Radio.Button value="table">Table</Radio.Button>
                      </Radio.Group>
                      {viewExchVolMix === 'chart' ? (
                        <DownloadButton onPng={() => exportCardAsPng('chart-exch-vol-mix', `Exchange_Volume_Mix_${dayjs().format('MMMYYYY')}`)} />
                      ) : (
                        <DownloadButton onCsv={() => exportTableAsCsv(tableDataAnalysis, columnsExchVolMix, `Exchange_Volume_Mix_${dayjs().format('MMMYYYY')}`)} />
                      )}
                      <Button icon={expandedChart === 'chart_exch_vol_mix' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart_exch_vol_mix' ? null : 'chart_exch_vol_mix')} />
                    </div>
                  }>
                  {viewExchVolMix === 'chart' ? (
                    <div style={{ height: expandedChart === 'chart_exch_vol_mix' ? 'calc(100vh - 120px)' : 360 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={analysisDataFormatted} margin={{ left: 10, right: 10, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="period" interval={axisInterval} height={80} tick={axisTickProps} tickFormatter={formatTick} />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Number(v).toFixed(2)}`} label={{ value: 'Volume (MU)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                          <Tooltip itemSorter={tooltipSorter} shared={true} labelFormatter={(label) => {
                            if (f.granularity === '15min-raw') {
                              const d = dayjs(label);
                              return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                            }
                            if (isHourlyOr15Min) return `Time: ${label}${f.granularity === 'hourly' ? ':00' : ''}`;
                            const d = dayjs(label);
                            if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                            if (f.granularity === 'yearly') return d.format('YYYY');
                            return d.format('DD MMM, YYYY');
                          }} formatter={(v, n) => [`${Number(v).toFixed(3)} MU`, n]} />
                          <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 30 }} />
                          <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />
                          {metricsExchVolMix.includes('gdam_vol_mu') && <Area type="monotone" dataKey="gdam_vol_mu" name="GDAM Volume" stackId="1" stroke="#2ECC71" fill="#2ECC71" fillOpacity={0.6} />}
                          {metricsExchVolMix.includes('dam_vol_mu') && <Area type="monotone" dataKey="dam_vol_mu" name="DAM Volume" stackId="1" stroke="#E67E22" fill="#E67E22" fillOpacity={0.6} />}
                          {metricsExchVolMix.includes('rtm_vol_mu') && <Area type="monotone" dataKey="rtm_vol_mu" name="RTM Volume" stackId="1" stroke="#3498DB" fill="#3498DB" fillOpacity={0.6} />}
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <Table dataSource={tableDataAnalysis} columns={columnsExchVolMix} size="small" scroll={{ x: 'max-content', y: 280 }} pagination={false} rowKey="period" />
                  )}
                </Card>

                {/* Chart: Bilateral vs. Exchange Volume */}
                <Card id="chart-bilat-exch-vol"
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span>Bilateral vs. Exchange Volume</span>
                    </div>
                  }
                  size="small" style={expandedChart === 'chart_bilat_exch_vol' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                  extra={
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <Select mode="multiple" size="small" value={metricsBilatExchVol} onChange={setMetricsBilatExchVol} style={{ width: 220 }} maxTagCount={1}
                        options={[
                          { label: 'Bilateral Volume', value: 'bilat_vol_mu' },
                          { label: 'Exchange Volume', value: 'exch_vol_mu' },
                        ]} />
                      <Radio.Group value={viewBilatExchVol} onChange={e => setViewBilatExchVol(e.target.value)} size="small">
                        <Radio.Button value="chart">Chart</Radio.Button>
                        <Radio.Button value="table">Table</Radio.Button>
                      </Radio.Group>
                      {viewBilatExchVol === 'chart' ? (
                        <DownloadButton onPng={() => exportCardAsPng('chart-bilat-exch-vol', `Bilateral_vs_Exchange_Volume_${dayjs().format('MMMYYYY')}`)} />
                      ) : (
                        <DownloadButton onCsv={() => exportTableAsCsv(tableDataAnalysis, columnsBilatExchVol, `Bilateral_vs_Exchange_Volume_${dayjs().format('MMMYYYY')}`)} />
                      )}
                      <Button icon={expandedChart === 'chart_bilat_exch_vol' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart_bilat_exch_vol' ? null : 'chart_bilat_exch_vol')} />
                    </div>
                  }>
                  {viewBilatExchVol === 'chart' ? (
                    <div style={{ height: expandedChart === 'chart_bilat_exch_vol' ? 'calc(100vh - 120px)' : 360 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={analysisDataFormatted} margin={{ left: 10, right: 10, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="period" interval={axisInterval} height={80} tick={axisTickProps} tickFormatter={formatTick} />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Number(v).toFixed(2)}`} label={{ value: 'Volume (MU)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                          <Tooltip itemSorter={tooltipSorter} shared={true} labelFormatter={(label) => {
                            if (f.granularity === '15min-raw') {
                              const d = dayjs(label);
                              return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                            }
                            if (isHourlyOr15Min) return `Time: ${label}${f.granularity === 'hourly' ? ':00' : ''}`;
                            const d = dayjs(label);
                            if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                            if (f.granularity === 'yearly') return d.format('YYYY');
                            return d.format('DD MMM, YYYY');
                          }} formatter={(val, name, item) => {
                            const payload = item?.payload || {};
                            const bilatMU = Number(payload.bilat_vol_mu) || 0;
                            const exchMU = Number(payload.exch_vol_mu) || 0;
                            const totMU = bilatMU + exchMU;
                            const exchPct = totMU > 0 ? (exchMU / totMU) * 100 : 0;
                            const bilatPct = totMU > 0 ? (bilatMU / totMU) * 100 : 0;
                            if (name === 'Exchange Volume' || name === 'Exchange' || name === 'exch_vol_mu') {
                              return [`${Number(val).toFixed(3)} MU (${exchPct.toFixed(1)}%)`, 'Exchange Volume'];
                            }
                            if (name === 'Bilateral Volume' || name === 'Bilateral' || name === 'bilat_vol_mu') {
                              return [`${Number(val).toFixed(3)} MU (${bilatPct.toFixed(1)}%)`, 'Bilateral Volume'];
                            }
                            return [`${Number(val).toFixed(3)} MU`, name];
                          }} />
                          <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 30 }} />
                          <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />
                          {metricsBilatExchVol.includes('bilat_vol_mu') && <Area type="monotone" dataKey="bilat_vol_mu" name="Bilateral Volume" stackId="1" stroke="#1E40AF" fill="#1E40AF" fillOpacity={0.7} />}
                          {metricsBilatExchVol.includes('exch_vol_mu') && <Area type="monotone" dataKey="exch_vol_mu" name="Exchange Volume" stackId="1" stroke="#EA580C" fill="#EA580C" fillOpacity={0.7} />}
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <Table dataSource={tableDataAnalysis} columns={columnsBilatExchVol} size="small" scroll={{ x: 'max-content', y: 280 }} pagination={false} rowKey="period" />
                  )}
                </Card>

                {/* Chart 4: Net Revenue by Market Segment */}
                <Card id="chart-rev-segment"
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span>Net Revenue by Market Segment</span>
                      <Button type="text" size="small" icon={<LeftOutlined />} onClick={() => setViz2('trend')} disabled={viz2 === 'trend'} />
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>{viz2 === 'trend' ? 'Trend Chart' : 'Pie Chart'}</Typography.Text>
                      <Button type="text" size="small" icon={<RightOutlined />} onClick={() => setViz2('pie')} disabled={viz2 === 'pie'} />
                    </div>
                  }
                  size="small" style={expandedChart === 'chart2' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                  extra={
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <Radio.Group value={stackMode} onChange={e => setStackMode(e.target.value)} size="small" optionType="button" buttonStyle="solid">
                        <Radio.Button value="absolute">Absolute</Radio.Button>
                        <Radio.Button value="expand">% Share</Radio.Button>
                      </Radio.Group>
                      <Radio.Group value={view2} onChange={e => setView2(e.target.value)} size="small">
                        <Radio.Button value="chart">Chart</Radio.Button>
                        <Radio.Button value="table">Table</Radio.Button>
                      </Radio.Group>
                      {view2 === 'chart' ? (
                        <DownloadButton onPng={() => exportCardAsPng('chart-rev-segment', `Revenue_Segment_${dayjs().format('MMMYYYY')}`)} />
                      ) : (
                        <DownloadButton onCsv={() => exportTableAsCsv(tableData2, columns2, `Revenue_Segment_${dayjs().format('MMMYYYY')}`)} />
                      )}
                      <Button icon={expandedChart === 'chart2' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart2' ? null : 'chart2')} />
                    </div>
                  }>
                  {view2 === 'chart' ? (
                    <div style={{ height: expandedChart === 'chart2' ? 'calc(100vh - 120px)' : 340 }}>
                      {viz2 === 'pie' ? (
                        <DonutChartWithLegend data={pieData2} colors={colors2} title="Total Revenue" segmentRates={segmentRates} />
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chart2Data} stackOffset={stackMode === 'expand' ? 'expand' : 'none'} margin={{ left: 10, right: 10, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" interval={axisInterval} height={80} tick={axisTickProps} tickFormatter={formatTick} />
                            <YAxis
                              tick={{ fontSize: 11 }}
                              tickFormatter={(tick) => stackMode === 'expand' ? `${(tick * 100).toFixed(0)}%` : formatCurrencyKM(tick)}
                              label={{ value: stackMode === 'expand' ? 'Share (%)' : 'Net Revenue (₹)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
                              domain={stackMode === 'expand' ? [0, 1] : [0, 'auto']}
                              allowDataOverflow={true}
                            />
                            <Tooltip itemSorter={tooltipSorter} shared={true} labelFormatter={(label) => {
                              if (f.granularity === '15min-raw') {
                                const d = dayjs(label);
                                return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                              }
                              if (isHourlyOr15Min) return `Time: ${label}${f.granularity === 'hourly' ? ':00' : ''}`;
                              const d = dayjs(label);
                              if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                              if (f.granularity === 'yearly') return d.format('YYYY');
                              return d.format('DD MMM, YYYY');
                            }} formatter={(v, n, entry) => {
                              if (stackMode === 'expand') {
                                const payload = entry?.payload || {};
                                const total = (payload.net_exch || 0) + (payload.contracts || 0);
                                const pct = total > 0 ? (Number(v) / total) * 100 : 0;
                                return [`${pct.toFixed(2)}%`, n];
                              }
                              return [formatValueKM(v, 2), n];
                            }} />
                            <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 30 }} />
                            <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />
                            <Bar dataKey="net_exch" name="Exchange" stackId="1" fill="#8E44AD" />
                            <Bar dataKey="contracts" name="Contracts" stackId="1" fill="#3498DB" />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  ) : (
                    <Table dataSource={tableData2} columns={columns2} size="small" scroll={{ x: 'max-content', y: 280 }} pagination={false} rowKey="period" />
                  )}
                </Card>

                {/* Chart 5: Charges & Deductions Breakdown */}
                <Card id="chart-rev-charges"
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span>Charges & Deductions Breakdown</span>
                      <Button type="text" size="small" icon={<LeftOutlined />} onClick={() => setViz3('trend')} disabled={viz3 === 'trend'} />
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>{viz3 === 'trend' ? 'Trend Chart' : 'Pie Chart'}</Typography.Text>
                      <Button type="text" size="small" icon={<RightOutlined />} onClick={() => setViz3('pie')} disabled={viz3 === 'pie'} />
                    </div>
                  }
                  size="small" style={expandedChart === 'chart3' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0 } : { marginBottom: 16 }}
                  extra={
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <Select size="small" value={contractFilter} onChange={setContractFilter} style={{ width: 160 }}
                        options={[
                          { label: 'All Contracts', value: 'all' },
                          { label: 'Exchange', value: 'exch' },
                          ...availableContracts.map(c => ({
                            label: c.name,
                            value: String(c.id)
                          }))
                        ]} />
                      <Select mode="multiple" size="small" value={metrics3} onChange={setMetrics3} style={{ width: 220 }} maxTagCount={1}
                        options={[
                          { label: 'STU Charges', value: 'stu_charges' },
                          { label: 'Scheduling Charges', value: 'nldc_fees' },
                          { label: "Trader's Comm.", value: 'trader_comm' },
                          { label: 'Exchange Comm.', value: 'exch_comm' },
                          { label: 'Tax', value: 'tax' },
                          { label: 'Rebate', value: 'rebate' }
                        ]} />
                      <Radio.Group value={view3} onChange={e => setView3(e.target.value)} size="small">
                        <Radio.Button value="chart">Chart</Radio.Button>
                        <Radio.Button value="table">Table</Radio.Button>
                      </Radio.Group>
                      {view3 === 'chart' ? (
                        <DownloadButton onPng={() => exportCardAsPng('chart-rev-charges', `Revenue_Charges_${dayjs().format('MMMYYYY')}`)} />
                      ) : (
                        <DownloadButton onCsv={() => exportTableAsCsv(tableData3, columns3, `Revenue_Charges_${dayjs().format('MMMYYYY')}`)} />
                      )}
                      <Button icon={expandedChart === 'chart3' ? <FullscreenExitOutlined /> : <FullscreenOutlined />} size="small" onClick={() => setExpandedChart(expandedChart === 'chart3' ? null : 'chart3')} />
                    </div>
                  }>
                  {view3 === 'chart' ? (
                    <div>
                      <div style={{ height: expandedChart === 'chart3' ? 'calc(100vh - 140px)' : 340 }}>
                        {viz3 === 'pie' ? (
                          <DonutChartWithLegend data={pieData3} colors={colors3} title="Total Charges" percentageBaseValue={totalGrossRevenue} totalVolume={totalVolume3} />
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={chart3Data} margin={{ left: 10, right: 10, bottom: 20 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="period" interval={axisInterval} height={80} tick={axisTickProps} tickFormatter={formatTick} />
                              <YAxis tick={{ fontSize: 11 }} tickFormatter={formatCurrencyKM} label={{ value: 'Amount (₹)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                              <Tooltip itemSorter={tooltipSorter} shared={false} labelFormatter={(label) => {
                                if (f.granularity === '15min-raw') {
                                  const d = dayjs(label);
                                  return d.isValid() ? d.format('DD-MMM-YYYY HH:mm') : label;
                                }
                                if (isHourlyOr15Min) return `Time: ${label}${f.granularity === 'hourly' ? ':00' : ''}`;
                                const d = dayjs(label);
                                if (f.granularity === 'monthly') return d.isValid() ? d.format('MMM-YY') : label;
                                if (f.granularity === 'yearly') return d.format('YYYY');
                                return d.format('DD MMM, YYYY');
                              }} formatter={(v, n) => [formatValueKM(v, 2), n]} />
                              <Legend content={renderCustomLegend} wrapperStyle={{ width: '100%', left: 30 }} />
                              <Brush dataKey="period" height={20} stroke="#888" {...brushProps} />
                              {metrics3.includes('stu_charges') && <Line type="monotone" dataKey="stu_charges" name="STU Charges" stroke="#E74C3C" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                              {metrics3.includes('nldc_fees') && <Line type="monotone" dataKey="nldc_fees" name="Scheduling Charges" stroke="#3498DB" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                              {metrics3.includes('trader_comm') && <Line type="monotone" dataKey="trader_comm" name="Trader's Comm." stroke="#F39C12" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                              {metrics3.includes('exch_comm') && <Line type="monotone" dataKey="exch_comm" name="Exchange Comm." stroke="#16A085" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                              {metrics3.includes('tax') && <Line type="monotone" dataKey="tax" name="Tax" stroke="#2980B9" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />}
                              {metrics3.includes('rebate') && <Line type="monotone" dataKey="rebate" name="Rebate" stroke="#8E44AD" strokeWidth={2} dot={false} activeDot={{ r: 6 }} />}
                            </ComposedChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                      {viz3 === 'pie' && (
                        <div style={{ fontSize: 11, color: '#666', fontStyle: 'italic', marginTop: 4, textAlign: 'center' }}>
                          * Percentages are calculated with respect to gross revenue
                        </div>
                      )}
                    </div>
                  ) : (
                    <Table dataSource={tableData3} columns={columns3} size="small" scroll={{ x: 'max-content', y: 280 }} pagination={false} rowKey="period" />
                  )}
                </Card>
              </Spin>
            )
          }
        ]} />
      </div>

      {/* Right Section: Filters Panel */}
      {activeTab === 'analysis' && <GlobalFilterBar onApply={fetchData} isRevenue={true} />}
    </div>
  );
}
